import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  buildChildrensBookUnitDiagnostics,
} from "../utils/childrensBookUnitDiagnostics";

import {
  buildChildrensBookPhysicalPageItems,
  childrensBookTotalPhysicalPages,
  childrensBookWriterPageLabel,
} from "../utils/childrensBookPhysicalPagination";

import ChildrensBookManuscriptEditor from "./ChildrensBookManuscriptEditor";
import ChildrensBookAuthoringImageSurface from "./ChildrensBookAuthoringImageSurface";
import ChildrensBookIllustrationAssetPanel from "./ChildrensBookIllustrationAssetPanel";
import ChildrensBookManuscriptIllustrationContext from "./ChildrensBookManuscriptIllustrationContext";

import {
  childrensBookAuthoringPageAspectRatio,
} from "../utils/childrensBookAuthoringPageGeometry";

import {
  prepareChildrensBookSharedManuscriptEdit,
} from "../utils/childrensBookSharedManuscriptEdit";

const EMPTY_UNIT = {
  id: "",
  unitType: "single_page",
  workingTitle: "",
  purpose: "",
  pageNumber: "",
  textAmount: "short",
  illustrationPlan: "small_illustration",
  illustrationPageSide: "",
  illustrationNote: "",
  layoutIntention: "",
  pageComposition: {
    title: "",
    subtitle: "",
    caption: "",
    creditLine: "",
  },
  status: "not_planned",
};


export function childrensBookFullSpreadUnitConflict(
  unit = {}
) {
  const unitType =
    String(
      unit?.unitType ?? ""
    )
      .trim()
      .toLowerCase();

  const illustrationPlan =
    String(
      unit?.illustrationPlan ?? ""
    )
      .trim()
      .toLowerCase();

  return (
    illustrationPlan ===
      "full_spread" &&
    unitType !==
      "double_page_spread"
  );
}


export function childrensBookIllustrationOptionsForUnit(
  options = [],
  unit = {}
) {
  const safeOptions =
    Array.isArray(options)
      ? options
      : [];

  const unitType =
    String(
      unit?.unitType ?? ""
    )
      .trim()
      .toLowerCase();

  if (
    unitType ===
      "double_page_spread"
  ) {
    return safeOptions;
  }

  const compatibleOptions =
    safeOptions.filter(
      (option) =>
        String(
          option?.value ?? ""
        )
          .trim()
          .toLowerCase() !==
        "full_spread"
    );

  /*
   * Preserve visibility of an already-existing
   * incompatible value. The writer must choose
   * deliberately how to resolve it; we never
   * silently rewrite stored planning authority.
   */
  if (
    childrensBookFullSpreadUnitConflict(
      unit
    )
  ) {
    const existingFullSpread =
      safeOptions.find(
        (option) =>
          String(
            option?.value ?? ""
          )
            .trim()
            .toLowerCase() ===
          "full_spread"
      );

    return existingFullSpread
      ? [
          ...compatibleOptions,
          existingFullSpread,
        ]
      : compatibleOptions;
  }

  return compatibleOptions;
}


/*
 * A historical Full Page / No-text unit has no exposed
 * manuscript area.
 *
 * When the writer deliberately changes that page to a
 * text-sharing illustration preset, reopen manuscript
 * eligibility before Save Page Changes freezes the unit.
 *
 * "short" is planning metadata only. Physical rendered
 * measurement remains authoritative for actual capacity.
 */
export function childrensBookIllustrationPlanChangeForUnit(
  unit = {},
  nextIllustrationPlan = ""
) {
  const current =
    unit &&
    typeof unit ===
      "object" &&
    !Array.isArray(
      unit
    )
      ? unit
      : {};

  const previousPlan =
    String(
      current
        .illustrationPlan ||
      ""
    )
      .trim()
      .toLowerCase();

  const nextPlan =
    String(
      nextIllustrationPlan ||
      ""
    )
      .trim()
      .toLowerCase();

  const currentTextAmount =
    String(
      current
        .textAmount ||
      ""
    )
      .trim()
      .toLowerCase();

  const reopensText =
    previousPlan ===
      "full_page" &&
    currentTextAmount ===
      "none" &&
    [
      "small_illustration",
      "half_page",
    ].includes(
      nextPlan
    );

  return {
    ...current,

    illustrationPlan:
      nextIllustrationPlan,

    ...(
      reopensText
        ? {
            textAmount:
              "short",
          }
        : {}
    ),

    illustrationPageSide:
      nextPlan ===
        "full_page" &&
      current
        .illustrationPageSide ===
        "with_text"
        ? ""
        : current
            .illustrationPageSide,
  };
}


/*
 * Variable image geometry is authoritative.
 *
 * If a historical Full Page / No-text image is physically
 * reduced so that it no longer fills the trim box, the
 * source page must become eligible to receive whatever
 * manuscript the physical measurement says will fit.
 */
export function childrensBookAuthoringGeometryChangeForUnit(
  unit = {},
  authoringImageGeometry = null
) {
  const current =
    unit &&
    typeof unit ===
      "object" &&
    !Array.isArray(
      unit
    )
      ? unit
      : {};

  const geometry =
    authoringImageGeometry &&
    typeof authoringImageGeometry ===
      "object" &&
    !Array.isArray(
      authoringImageGeometry
    )
      ? authoringImageGeometry
      : null;

  const widthFraction =
    Number(
      geometry
        ?.widthFraction
    );

  const heightFraction =
    Number(
      geometry
        ?.heightFraction
    );

  const leavesTextRoom =
    Number.isFinite(
      widthFraction
    ) &&
    Number.isFinite(
      heightFraction
    ) &&
    widthFraction > 0 &&
    heightFraction > 0 &&
    (
      widthFraction <
        0.999999 ||
      heightFraction <
        0.999999
    );

  const reopensText =
    String(
      current
        .illustrationPlan ||
      ""
    )
      .trim()
      .toLowerCase() ===
        "full_page" &&
    String(
      current
        .textAmount ||
      ""
    )
      .trim()
      .toLowerCase() ===
        "none" &&
    leavesTextRoom;

  return {
    ...current,

    authoringImageGeometry,

    ...(
      reopensText
        ? {
            textAmount:
              "short",
          }
        : {}
    ),
  };
}


function Field({
  label,
  help,
  value,
  onChange,
  placeholder = "",
  multiline = false,
}) {
  const sharedStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #cbd5e1",
    font: "inherit",
  };

  return (
    <label
      style={{
        display: "grid",
        gap: 7,
        fontWeight: 800,
        color: "#334155",
      }}
    >
      <span>{label}</span>
      {help && (
        <span
          style={{
            fontSize: 13,
            lineHeight: 1.45,
            fontWeight: 500,
            color: "#64748b",
          }}
        >
          {help}
        </span>
      )}
      {multiline ? (
        <textarea
          value={value}
          placeholder={placeholder}
          rows={4}
          onChange={(event) =>
            onChange(event.target.value)
          }
          style={{
            ...sharedStyle,
            resize: "vertical",
          }}
        />
      ) : (
        <input
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={(event) =>
            onChange(event.target.value)
          }
          style={{
            ...sharedStyle,
            minHeight: 44,
          }}
        />
      )}
    </label>
  );
}

function SelectField({
  label,
  help,
  value,
  onChange,
  options,
}) {
  return (
    <label
      style={{
        display: "grid",
        gap: 7,
        fontWeight: 800,
        color: "#334155",
      }}
    >
      <span>{label}</span>
      {help && (
        <span
          style={{
            fontSize: 13,
            lineHeight: 1.45,
            fontWeight: 500,
            color: "#64748b",
          }}
        >
          {help}
        </span>
      )}
      <select
        value={value}
        onChange={(event) =>
          onChange(event.target.value)
        }
        style={{
          width: "100%",
          minHeight: 44,
          padding: "10px 12px",
          borderRadius: 10,
          border: "1px solid #cbd5e1",
          background: "#ffffff",
          font: "inherit",
        }}
      >
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
          >
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

// Rendering boundary only. This does not limit book size.
// Book Structure displays at most 20 page/spread cards at once.
const BOOK_STRUCTURE_RANGE_SIZE = 20;


function normalizedBookStructureRangeStart(
  value,
  totalUnits
) {
  const total =
    Math.max(
      0,
      Number(totalUnits) || 0
    );

  if (total < 1) {
    return 0;
  }

  const requested =
    Number(value);

  const aligned =
    Number.isInteger(requested) &&
    requested >= 0
      ? Math.floor(
          requested /
            BOOK_STRUCTURE_RANGE_SIZE
        ) *
          BOOK_STRUCTURE_RANGE_SIZE
      : 0;

  const maximum =
    Math.floor(
      (total - 1) /
        BOOK_STRUCTURE_RANGE_SIZE
    ) *
      BOOK_STRUCTURE_RANGE_SIZE;

  return Math.min(
    aligned,
    maximum
  );
}

export default function ChildrensBookPageSpreadPlanning({
  lang = "en",
  bookMap,
  manuscriptPlan = {},
  projectPlan,
  childrensBookAssets = [],
  assetManagementEnabled = false,
  childrensBookAssetsLoading = false,
  childrensBookAssetsError = "",
  childrensBookAssetActionId = "",
  onLoadChildrensBookAssetFile,
  onUploadChildrensBookIllustration,
  onRemoveChildrensBookAsset,
  persistenceEnabled = false,
  initialUnitId = "",
  returnToWholeBookReview = false,
  onChange,
  onManuscriptChange,
  onApplyStructuralManuscriptTransition,
  structuralManuscriptTransitionSaving = false,
  onReturn,
  onExit,
}) {
  const isFR =
    String(lang).toLowerCase() === "fr";

  const units = useMemo(
    () =>
      Array.isArray(bookMap.units)
        ? bookMap.units
        : [],
    [bookMap.units]
  );

  const [
    bookStructureRangeStart,
    setBookStructureRangeStart,
  ] = useState(() => {
    const requestedId =
      String(
        initialUnitId || ""
      ).trim();

    const requestedIndex =
      units.findIndex(
        (unit) =>
          String(
            unit?.id || ""
          ).trim() ===
          requestedId
      );

    return requestedIndex >= 0
      ? Math.floor(
          requestedIndex /
            BOOK_STRUCTURE_RANGE_SIZE
        ) *
          BOOK_STRUCTURE_RANGE_SIZE
      : 0;
  });

  const boundedBookStructureRangeStart =
    normalizedBookStructureRangeStart(
      bookStructureRangeStart,
      units.length
    );

  const visibleBookStructureRows =
    useMemo(
      () =>
        units
          .slice(
            boundedBookStructureRangeStart,
            boundedBookStructureRangeStart +
              BOOK_STRUCTURE_RANGE_SIZE
          )
          .map(
            (unit, visibleIndex) => ({
              unit,
              index:
                boundedBookStructureRangeStart +
                visibleIndex,
            })
          ),
      [
        boundedBookStructureRangeStart,
        units,
      ]
    );

  const bookStructureRangeCount =
    Math.max(
      1,
      Math.ceil(
        units.length /
          BOOK_STRUCTURE_RANGE_SIZE
      )
    );

  const [
    movingUnitId,
    setMovingUnitId,
  ] = useState("");

  const [
    movePositionValue,
    setMovePositionValue,
  ] = useState("");

  const [
    draggingUnitId,
    setDraggingUnitId,
  ] = useState("");

  const [
    dragOverUnitId,
    setDragOverUnitId,
  ] = useState("");

  const unitDiagnostics =
    useMemo(
      () =>
        buildChildrensBookUnitDiagnostics(
          projectPlan,
          childrensBookAssets,
          {
            lang: isFR
              ? "fr"
              : "en",
          }
        ),
      [
        projectPlan,
        childrensBookAssets,
        isFR,
      ]
    );

  const attentionIssuesByUnit =
    useMemo(
      () => {
        const result = {};

        [
          ...unitDiagnostics.manuscript,
          ...unitDiagnostics
            .illustrationPlan,
          ...unitDiagnostics.assembly.filter(
            (item) =>
              item.kind ===
              "missing_illustration_asset"
          ),
        ].forEach(
          (issue) => {
            const unitId =
              String(
                issue?.unitId || ""
              ).trim();

            if (!unitId) {
              return;
            }

            if (!result[unitId]) {
              result[unitId] = [];
            }

            if (
              !result[unitId].some(
                (existing) =>
                  existing.message ===
                  issue.message
              )
            ) {
              result[unitId].push(
                issue
              );
            }
          }
        );

        return result;
      },
      [unitDiagnostics]
    );

  const [editingUnit, setEditingUnit] =
    useState(null);

  const [
    frontMatterChoicesOpen,
    setFrontMatterChoicesOpen,
  ] = useState(false);

  const manuscriptEntries =
    useMemo(
      () =>
        Array.isArray(
          manuscriptPlan
            ?.entries
        )
          ? manuscriptPlan
              .entries
          : [],
      [
        manuscriptPlan
          ?.entries,
      ]
    );

  const manuscriptSelectionHandoffRef =
    useRef(
      new Map()
    );

  const [
    manuscriptSelectionRestore,
    setManuscriptSelectionRestore,
  ] = useState(null);

  const manuscriptSelectionRestoreRevisionRef =
    useRef(0);

  /*
   * Save Page Changes may wait for physical layout
   * measurement. Track the exact current form state so
   * an older result can never replace newer writer edits.
   */
  const latestEditingUnitSignatureRef =
    useRef("");

  latestEditingUnitSignatureRef.current =
    JSON.stringify(
      editingUnit
    );

  useEffect(
    () => () => {
      /*
       * A result resolving after this editor disappears
       * is no longer current for the writer's page form.
       */
      latestEditingUnitSignatureRef.current =
        "__childrens_book_editor_unmounted__";
    },
    []
  );

  const [
    savedUnitNotice,
    setSavedUnitNotice,
  ] = useState("");

  const [
    structuralReflowError,
    setStructuralReflowError,
  ] = useState("");

  const [
    structuralReflowApplying,
    setStructuralReflowApplying,
  ] = useState(false);

  const [
    pendingRemovalId,
    setPendingRemovalId,
  ] = useState(null);

  const [reviewingMap, setReviewingMap] =
    useState(false);

  const initialUnitHandledRef =
    useRef("");

  useEffect(() => {
    const unitId =
      String(
        initialUnitId || ""
      ).trim();

    if (
      !unitId ||
      initialUnitHandledRef.current ===
        unitId
    ) {
      return;
    }

    const targetUnit =
      units.find(
        (unit) =>
          String(
            unit?.id || ""
          ).trim() === unitId
      );

    if (!targetUnit) {
      return;
    }

    initialUnitHandledRef.current =
      unitId;

    setReviewingMap(false);
    setSavedUnitNotice("");

    setEditingUnit(
      targetUnit
    );
  }, [
    initialUnitId,
    units,
  ]);


  const unitTypeOptions = [
    { value: "single_page", label: isFR ? "Une page" : "One page" },
    { value: "double_page_spread", label: isFR ? "Deux pages" : "Two pages" },
    { value: "front_matter", label: isFR ? "Page liminaire" : "Front matter page" },
    { value: "back_matter", label: isFR ? "Page finale" : "Back matter page" },
    { value: "activity_page", label: isFR ? "Page d’activité" : "Activity page" },
  ];

  const textAmountOptions = [
    { value: "none", label: isFR ? "Aucun texte" : "No text" },
    { value: "very_short", label: isFR ? "Très court" : "Very short" },
    { value: "short", label: isFR ? "Court" : "Short" },
    { value: "medium", label: isFR ? "Moyen" : "Medium" },
    { value: "long", label: isFR ? "Long" : "Long" },
  ];

  const illustrationOptions = [
    { value: "none", label: isFR ? "Aucune illustration" : "No illustration" },
    { value: "small_illustration", label: isFR ? "Petite illustration" : "Small illustration" },
    { value: "half_page", label: isFR ? "Illustration sur une demi-page" : "Half-page illustration" },
    { value: "full_page", label: isFR ? "Illustration pleine page" : "Full-page illustration" },
    { value: "full_spread", label: isFR ? "Illustration sur toute la double page" : "Full-spread illustration" },
    { value: "integrated_text", label: isFR ? "Texte intégré à l’illustration" : "Text integrated with illustration" },
  ];

  const editingIllustrationOptions =
    childrensBookIllustrationOptionsForUnit(
      illustrationOptions,
      editingUnit || {}
    );

  const statusOptions = [
    { value: "not_planned", label: isFR ? "Non planifié" : "Not Planned" },
    { value: "planned", label: isFR ? "Planifié" : "Planned" },
    { value: "ready_for_writing", label: isFR ? "Prêt pour la rédaction" : "Ready for Writing" },
    { value: "ready_for_illustration", label: isFR ? "Prêt pour l’illustration" : "Ready for Illustration" },
    { value: "complete", label: isFR ? "Terminé" : "Complete" },
  ];

  function labelFor(options, value) {
    return options.find(
      (option) => option.value === value
    )?.label || value;
  }

  function displayValue(value) {
    const normalized =
      String(value ?? "").trim();

    return normalized || (
      isFR ? "Non renseigné" : "Not provided"
    );
  }

  function renderUnitAttention(
    unit
  ) {
    const issues =
      attentionIssuesByUnit[
        String(
          unit?.id || ""
        ).trim()
      ] || [];

    if (issues.length === 0) {
      return null;
    }

    return (
      <div
        className="ns-nbb-unit-attention"
        style={{
          marginTop: 10,
          padding: "9px 11px",
          borderRadius: 10,
          border:
            "1px solid rgba(217,119,6,0.34)",
          background: "#fffbeb",
          color: "#92400e",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            padding: "3px 7px",
            borderRadius: 999,
            background: "#fef3c7",
            color: "#92400e",
            fontSize: 11,
            fontWeight: 900,
          }}
        >
          {isFR
            ? "À compléter"
            : "Needs attention"}
        </span>

        <div
          style={{
            display: "grid",
            gap: 4,
            marginTop: 7,
            fontSize: 12,
            lineHeight: 1.45,
            fontWeight: 650,
          }}
        >
          {issues.map(
            (issue) => (
              <div
                key={
                  `${issue.kind}-${issue.message}`
                }
              >
                {issue.message}
              </div>
            )
          )}
        </div>
      </div>
    );
  }

  const physicalPageItems =
    useMemo(
      () =>
        buildChildrensBookPhysicalPageItems(
          units
        ),
      [units]
    );

  function unitsWithDraftUnit(
    unit
  ) {
    if (!unit) {
      return units;
    }

    const unitId =
      String(
        unit?.id || ""
      ).trim();

    const existingIndex =
      units.findIndex(
        (candidate) =>
          String(
            candidate?.id || ""
          ).trim() === unitId
      );

    if (existingIndex < 0) {
      if (
        unit?.unitType ===
          "front_matter"
      ) {
        const firstNonFrontMatterIndex =
          units.findIndex(
            (candidate) =>
              candidate?.unitType !==
                "front_matter"
          );

        const insertionIndex =
          firstNonFrontMatterIndex ===
            -1
            ? units.length
            : firstNonFrontMatterIndex;

        return [
          ...units.slice(
            0,
            insertionIndex
          ),

          unit,

          ...units.slice(
            insertionIndex
          ),
        ];
      }

      return [
        ...units,
        unit,
      ];
    }

    return units.map(
      (
        candidate,
        index
      ) =>
        index ===
        existingIndex
          ? unit
          : candidate
    );
  }

  function physicalItemForUnit(
    unit,
    sourceUnits = null
  ) {
    if (!unit) {
      return null;
    }

    const nextUnits =
      Array.isArray(
        sourceUnits
      )
        ? sourceUnits
        : unitsWithDraftUnit(
            unit
          );

    const unitId =
      String(
        unit?.id || ""
      ).trim();

    const index =
      nextUnits.findIndex(
        (candidate) =>
          String(
            candidate?.id || ""
          ).trim() === unitId
      );

    if (index < 0) {
      return null;
    }

    return (
      buildChildrensBookPhysicalPageItems(
        nextUnits
      )[index] || null
    );
  }

  function unitReference(
    unit,
    sourceUnits = null
  ) {
    return (
      childrensBookWriterPageLabel(
        physicalItemForUnit(
          unit,
          sourceUnits
        )
      ) ||
      (
        isFR
          ? "Page du livre"
          : "Book page"
      )
    );
  }

  function picturePageOptionLabel(
    side,
    unit
  ) {
    const item =
      physicalItemForUnit(
        unit
      );

    const pageNumber =
      side === "left"
        ? item?.pageStart
        : item?.pageEnd;

    const sideLabel =
      side === "left"
        ? (
            isFR
              ? "Page gauche"
              : "Left page"
          )
        : (
            isFR
              ? "Page droite"
              : "Right page"
          );

    return pageNumber
      ? `${sideLabel} — Page ${pageNumber}`
      : sideLabel;
  }

  function unitSaveActionLabel(unit) {
    if (
      unit?.unitType ===
      "double_page_spread"
    ) {
      return isFR
        ? "Enregistrer les modifications de la double page"
        : "Save Spread Changes";
    }

    if (
      unit?.unitType ===
      "single_page"
    ) {
      return isFR
        ? "Enregistrer les modifications de la page"
        : "Save Page Changes";
    }

    return isFR
      ? "Enregistrer les modifications de la page spéciale"
      : "Save Special Page Changes";
  }

  const savedEditingUnit =
    editingUnit
      ? (
          units.find(
            (unit) =>
              unit.id ===
              editingUnit.id
          ) || null
        )
      : null;

  const editingExistingUnit =
    Boolean(savedEditingUnit);

  const hasUnsavedUnitChanges =
    Boolean(
      savedEditingUnit &&
      JSON.stringify(
        editingUnit
      ) !==
        JSON.stringify(
          savedEditingUnit
        )
    );


  const authoringPageAspectRatio =
    childrensBookAuthoringPageAspectRatio(
      projectPlan
        ?.visualFormat
    );

  const fullPageAuthoringSurfaceAvailable =
    Boolean(
      editingExistingUnit &&
      !hasUnsavedUnitChanges &&
      savedEditingUnit
        ?.unitType ===
        "single_page" &&
      String(
        savedEditingUnit
          ?.illustrationPlan ||
          ""
      )
        .trim()
        .toLowerCase() ===
        "full_page" &&
      authoringPageAspectRatio
    );


  const [
    transientFullPageAuthoringGeometry,
    setTransientFullPageAuthoringGeometry,
  ] = useState(
    null
  );

  const fullPageAuthoringGeometryRequestSequenceRef =
    useRef(
      0
    );

  useEffect(
    () => {
      fullPageAuthoringGeometryRequestSequenceRef
        .current +=
        1;

      setTransientFullPageAuthoringGeometry(
        null
      );
    },
    [
      savedEditingUnit
        ?.id,
      savedEditingUnit
        ?.illustrationPlan,
      authoringPageAspectRatio,
    ]
  );

  const persistedFullPageAuthoringGeometry =
    (() => {
      const candidate =
        savedEditingUnit
          ?.authoringImageGeometry;

      if (
        !candidate ||
        typeof candidate !==
          "object" ||
        Array.isArray(
          candidate
        )
      ) {
        return null;
      }

      const xFraction =
        Number(
          candidate.xFraction
        );
      const yFraction =
        Number(
          candidate.yFraction
        );
      const widthFraction =
        Number(
          candidate.widthFraction
        );
      const heightFraction =
        Number(
          candidate.heightFraction
        );

      if (
        !Number.isFinite(
          xFraction
        ) ||
        !Number.isFinite(
          yFraction
        ) ||
        !Number.isFinite(
          widthFraction
        ) ||
        !Number.isFinite(
          heightFraction
        ) ||
        xFraction < 0 ||
        yFraction < 0 ||
        widthFraction <= 0 ||
        heightFraction <= 0 ||
        xFraction +
          widthFraction >
          1.000001 ||
        yFraction +
          heightFraction >
          1.000001
      ) {
        return null;
      }

      return {
        xFraction,
        yFraction,
        widthFraction,
        heightFraction,
      };
    })();


  const fullPageAuthoringSurfaceGeometry =
    (
      transientFullPageAuthoringGeometry &&
      transientFullPageAuthoringGeometry
        .unitId ===
      savedEditingUnit
        ?.id
    )
      ? transientFullPageAuthoringGeometry
          .geometry
      : (
          persistedFullPageAuthoringGeometry ||
          {
            xFraction: 0,
            yFraction: 0,
            widthFraction: 1,
            heightFraction: 1,
          }
        );

  async function commitFullPageAuthoringGeometry(
    surfaceGeometry = {}
  ) {
    if (
      !fullPageAuthoringSurfaceAvailable ||
      !savedEditingUnit
        ?.id
    ) {
      return;
    }

    const xFraction =
      Number(
        surfaceGeometry
          ?.xFraction
      );

    const yFraction =
      Number(
        surfaceGeometry
          ?.yFraction
      );

    const widthFraction =
      Number(
        surfaceGeometry
          ?.widthFraction
      );

    const heightFraction =
      Number(
        surfaceGeometry
          ?.heightFraction
      );

    if (
      !Number.isFinite(
        xFraction
      ) ||
      !Number.isFinite(
        yFraction
      ) ||
      !Number.isFinite(
        widthFraction
      ) ||
      !Number.isFinite(
        heightFraction
      ) ||
      xFraction < 0 ||
      yFraction < 0 ||
      widthFraction <= 0 ||
      heightFraction <= 0 ||
      xFraction +
        widthFraction >
        1.000001 ||
      yFraction +
        heightFraction >
        1.000001
    ) {
      return;
    }

    const normalizedSurfaceGeometry = {
      xFraction,
      yFraction,
      widthFraction,
      heightFraction,
    };

    const previousTransientGeometry =
      transientFullPageAuthoringGeometry;

    const changedUnitId =
      String(
        savedEditingUnit
          .id
      ).trim();

    const requestUnitSignature =
      latestEditingUnitSignatureRef
        .current;

    const requestSequence =
      fullPageAuthoringGeometryRequestSequenceRef
        .current +
      1;

    fullPageAuthoringGeometryRequestSequenceRef.current =
      requestSequence;

    setTransientFullPageAuthoringGeometry({
      unitId:
        changedUnitId,

      geometry:
        normalizedSurfaceGeometry,
    });

    if (
      typeof onApplyStructuralManuscriptTransition !==
        "function"
    ) {
      return;
    }

    const authoringImageGeometry = {
      containerScope:
        "trim_box",

      ...normalizedSurfaceGeometry,

      wrapMode:
        "square",

      wrapSide:
        "largest",

      textGapPoints:
        0,

      preserveAspectRatio:
        true,
    };

    /*
     * Presets remain planning authority.
     * Exact writer geometry belongs to the
     * physical Book Structure unit.
     */
    const nextUnitWithAuthoringImageGeometry =
      childrensBookAuthoringGeometryChangeForUnit(
        savedEditingUnit,
        authoringImageGeometry
      );

    setStructuralReflowError(
      ""
    );

    setStructuralReflowApplying(
      true
    );

    let transitionResult;

    try {
      transitionResult =
        await onApplyStructuralManuscriptTransition({
          changedUnitId,

          nextUnit:
            nextUnitWithAuthoringImageGeometry,

          authoringImageGeometry,

          isRequestCurrent:
            () =>
              fullPageAuthoringGeometryRequestSequenceRef
                .current ===
                requestSequence &&
              latestEditingUnitSignatureRef
                .current ===
                requestUnitSignature,
        });
    } catch (error) {
      if (
        fullPageAuthoringGeometryRequestSequenceRef
          .current ===
        requestSequence
      ) {
        setTransientFullPageAuthoringGeometry(
          previousTransientGeometry
        );

        setStructuralReflowError(
          String(
            error?.message ||
            (
              isFR
                ? "Le redimensionnement de l’illustration n’a pas pu être vérifié. La taille précédente a été restaurée."
                : "The illustration resize could not be checked. The previous size was restored."
            )
          )
        );
      }

      return;
    } finally {
      if (
        fullPageAuthoringGeometryRequestSequenceRef
          .current ===
        requestSequence
      ) {
        setStructuralReflowApplying(
          false
        );
      }
    }

    if (
      fullPageAuthoringGeometryRequestSequenceRef
        .current !==
      requestSequence
    ) {
      return;
    }

    if (
      !transitionResult ||
      transitionResult
        .ok ===
        false
    ) {
      setTransientFullPageAuthoringGeometry(
        previousTransientGeometry
      );

      setStructuralReflowError(
        transitionResult
          ?.error ||
        (
          isFR
            ? "Le redimensionnement de l’illustration n’a pas pu être vérifié. La taille précédente a été restaurée."
            : "The illustration resize could not be checked. The previous size was restored."
        )
      );
    }

    if (
      transitionResult &&
      transitionResult.ok !==
        false
    ) {
      setEditingUnit(
        (current) => {
          if (
            String(
              current?.id ||
              ""
            ).trim() !==
            changedUnitId
          ) {
            return current;
          }

          return (
            childrensBookAuthoringGeometryChangeForUnit(
              current,
              authoringImageGeometry
            )
          );
        }
      );
    }

  }


  const manuscriptEditingEnabled =
    typeof onManuscriptChange ===
      "function";

  const editingUnitUsesNoText =
    String(
      savedEditingUnit
        ?.textAmount ||
      ""
    )
      .trim()
      .toLowerCase() ===
    "none";

  const manuscriptDraftingAvailable =
    Boolean(
      editingExistingUnit &&
      !hasUnsavedUnitChanges &&
      !editingUnitUsesNoText &&
      manuscriptEditingEnabled
    );

  function manuscriptEntryFor(
    unitId
  ) {
    const resolvedUnitId =
      String(
        unitId ||
        ""
      ).trim();

    return (
      manuscriptEntries.find(
        (entry) =>
          String(
            entry?.unitId ||
            ""
          ).trim() ===
          resolvedUnitId
      ) || {
        unitId:
          resolvedUnitId,
        manuscriptText: "",
        authorNote: "",
        status: "not_started",
      }
    );
  }

  function rememberBookStructureManuscriptSelection(
    unitId,
    requestedSelection
  ) {
    const resolvedUnitId =
      String(
        unitId ||
        ""
      ).trim();

    const blockId =
      String(
        requestedSelection
          ?.blockId ||
        ""
      ).trim();

    if (
      !resolvedUnitId ||
      !blockId
    ) {
      if (
        resolvedUnitId
      ) {
        manuscriptSelectionHandoffRef
          .current
          .delete(
            resolvedUnitId
          );
      }

      return null;
    }

    const start =
      Math.max(
        0,
        Number(
          requestedSelection
            ?.start
        ) || 0
      );

    const end =
      Math.max(
        0,
        Number(
          requestedSelection
            ?.end
        ) || 0
      );

    const token = {
      unitId:
        resolvedUnitId,

      selection: {
        blockId,
        start,
        end,
      },
    };

    manuscriptSelectionHandoffRef
      .current
      .set(
        resolvedUnitId,
        token
      );

    return token;
  }

  function handleBookStructureManuscriptChange(
    unitId,
    fields = {}
  ) {
    if (
      typeof onManuscriptChange !==
      "function"
    ) {
      return;
    }

    const resolvedUnitId =
      String(
        unitId ||
        ""
      ).trim();

    const sourceSavedUnit =
      units.find(
        (unit) =>
          String(
            unit?.id ||
            ""
          ).trim() ===
          resolvedUnitId
      ) || null;

    /*
     * Reflow must use the Book Structure that is
     * actually authoritative, not unsaved local
     * changes in the planning form.
     */
    if (
      !sourceSavedUnit ||
      String(
        editingUnit
          ?.id ||
        ""
      ).trim() !==
        resolvedUnitId ||
      JSON.stringify(
        editingUnit
      ) !==
        JSON.stringify(
          sourceSavedUnit
        ) ||
      String(
        sourceSavedUnit
          ?.textAmount ||
        ""
      )
        .trim()
        .toLowerCase() ===
        "none"
    ) {
      return;
    }

    const sourceSelectionToken =
      rememberBookStructureManuscriptSelection(
        resolvedUnitId,
        fields
          ?.selectionHandoff
      );

    setManuscriptSelectionRestore(
      null
    );

    const sharedEdit =
      prepareChildrensBookSharedManuscriptEdit({
        units,
        entries:
          manuscriptEntries,

        unitId:
          resolvedUnitId,

        manuscriptContent:
          fields
            ?.manuscriptContent,

        selectionHandoff:
          sourceSelectionToken
            ?.selection,

        editIntent:
          fields
            ?.editIntent,
      });

    if (
      !sharedEdit
        ?.ok
    ) {
      return;
    }

    if (
      !sharedEdit
        .flowApplied
    ) {
      onManuscriptChange(
        "entries",
        sharedEdit
          .nextEntries
      );

      return;
    }

    const targetUnitId =
      String(
        sharedEdit
          .targetUnitId ||
        ""
      ).trim();

    const restorableSelection =
      sharedEdit
        .restorableSelection;

    const targetEntrySignature =
      String(
        sharedEdit
          .targetEntrySignature ||
        ""
      );

    if (
      !targetUnitId ||
      !restorableSelection
        ?.selection ||
      !targetEntrySignature
    ) {
      return;
    }

    const targetIndex =
      units.findIndex(
        (unit) =>
          String(
            unit?.id ||
            ""
          ).trim() ===
          targetUnitId
      );

    if (
      targetIndex < 0
    ) {
      return;
    }

    const targetUnit =
      units[
        targetIndex
      ];

    manuscriptSelectionRestoreRevisionRef
      .current += 1;

    const selectionRestore = {
      unitId:
        targetUnitId,

      expectedEntrySignature:
        targetEntrySignature,

      revision:
        manuscriptSelectionRestoreRevisionRef
          .current,

      selection:
        restorableSelection
          .selection,
    };

    onManuscriptChange(
      "entries",
      sharedEdit
        .nextEntries
    );

    if (
      targetUnitId !==
      resolvedUnitId
    ) {
      setEditingUnit(
        targetUnit
      );

      setBookStructureRangeStart(
        Math.floor(
          targetIndex /
            BOOK_STRUCTURE_RANGE_SIZE
        ) *
          BOOK_STRUCTURE_RANGE_SIZE
      );
    }

    setManuscriptSelectionRestore(
      selectionRestore
    );
  }

  useEffect(() => {
    if (
      !manuscriptSelectionRestore
    ) {
      return;
    }

    if (
      String(
        manuscriptSelectionRestore
          ?.unitId ||
        ""
      ).trim() ===
        String(
          editingUnit
            ?.id ||
          ""
        ).trim()
    ) {
      return;
    }

    setManuscriptSelectionRestore(
      null
    );
  }, [
    editingUnit,
    manuscriptSelectionRestore,
  ]);


  function createUnit(unitType) {
    setSavedUnitNotice("");

    setFrontMatterChoicesOpen(
      false
    );

    setEditingUnit({
      ...EMPTY_UNIT,
      id: `unit-${Date.now()}-${units.length + 1}`,
      unitType,
      pageNumber: String(units.length + 1),
    });
  }


  function createFrontMatterUnit(
    kind
  ) {
    const starters = {
      title_page: {
        workingTitle:
          isFR
            ? "Page de titre"
            : "Title Page",

        purpose:
          isFR
            ? "Présenter le titre du livre et le nom d’auteur affiché."
            : "Present the book title and displayed author name.",

        textAmount:
          "very_short",
      },

      copyright_page: {
        workingTitle:
          isFR
            ? "Droits d’auteur"
            : "Copyright",

        purpose:
          isFR
            ? "Présenter les droits d’auteur et les informations de publication approuvés par l’auteur."
            : "Present creator-approved copyright and publication information.",

        textAmount:
          "short",
      },

      dedication_page: {
        workingTitle:
          isFR
            ? "Dédicace"
            : "Dedication",

        purpose:
          isFR
            ? "Ajouter une courte dédicace dans les propres mots de l’auteur."
            : "Add a short dedication in the author’s own words.",

        textAmount:
          "very_short",
      },

      contents: {
        workingTitle:
          isFR
            ? "Sommaire"
            : "Contents",

        purpose:
          isFR
            ? "Prévoir le sommaire du livre. Vérifiez les numéros de page après la pagination finale."
            : "Plan the book contents. Check page numbers after final pagination.",

        textAmount:
          "short",
      },

      other: {
        workingTitle:
          isFR
            ? "Autre page liminaire"
            : "Other Front Matter",

        purpose:
          isFR
            ? "Utiliser pour une préface, des remerciements, une note aux lecteurs ou une autre page liminaire."
            : "Use for a foreword, acknowledgements, note to readers, or other opening material.",

        textAmount:
          "short",
      },
    };

    const starter =
      starters[kind] ||
      starters.other;

    setSavedUnitNotice("");

    setFrontMatterChoicesOpen(
      false
    );

    setEditingUnit({
      ...EMPTY_UNIT,

      id:
        `unit-${Date.now()}-${units.length + 1}`,

      unitType:
        "front_matter",

      pageNumber:
        String(
          units.length + 1
        ),

      workingTitle:
        starter.workingTitle,

      purpose:
        starter.purpose,

      textAmount:
        starter.textAmount,

      illustrationPlan:
        "none",

      illustrationPageSide:
        "",

      illustrationNote:
        "",
    });
  }

  async function saveUnit() {
    if (!editingUnit) return;

    /*
     * Freeze the exact writer choice represented by this
     * Save Page Changes action.
     */
    const requestedUnit =
      JSON.parse(
        JSON.stringify(
          editingUnit
        )
      );

    const requestUnitSignature =
      JSON.stringify(
        requestedUnit
      );

    if (
      childrensBookFullSpreadUnitConflict(
        requestedUnit
      )
    ) {
      window.alert(
        isFR
          ? "Une illustration sur toute la double page nécessite une unité « Double page ». Choisissez une option d’illustration compatible ou changez le type d’élément."
          : "A full-spread illustration requires a Two-page spread. Choose a compatible illustration requirement or change the Planning unit to Two-page spread."
      );

      return;
    }

    const requiresPicturePageChoice =
      requestedUnit.unitType ===
        "double_page_spread" &&
      [
        "small_illustration",
        "half_page",
        "full_page",
      ].includes(
        requestedUnit.illustrationPlan
      );

    const validPicturePageChoices =
      requestedUnit.illustrationPlan ===
        "full_page"
        ? [
            "left",
            "right",
          ]
        : [
            "with_text",
            "left",
            "right",
          ];

    if (
      requiresPicturePageChoice &&
      !validPicturePageChoices.includes(
        requestedUnit
          .illustrationPageSide
      )
    ) {
      window.alert(
        isFR
          ? "Choisissez où l’image doit apparaître dans cette double page."
          : "Choose where the picture should appear in this two-page spread."
      );

      return;
    }

    const exists =
      units.some(
        (unit) =>
          unit.id ===
          requestedUnit.id
      );

    let nextUnits;

    if (exists) {
      nextUnits =
        units.map(
          (unit) =>
            unit.id ===
              requestedUnit.id
              ? requestedUnit
              : unit
        );
    } else if (
      requestedUnit.unitType ===
        "front_matter"
    ) {
      const firstNonFrontMatterIndex =
        units.findIndex(
          (unit) =>
            unit?.unitType !==
              "front_matter"
        );

      const insertionIndex =
        firstNonFrontMatterIndex ===
          -1
          ? units.length
          : firstNonFrontMatterIndex;

      nextUnits = [
        ...units.slice(
          0,
          insertionIndex
        ),

        requestedUnit,

        ...units.slice(
          insertionIndex
        ),
      ];
    } else {
      nextUnits = [
        ...units,
        requestedUnit,
      ];
    }

    const savedReference =
      unitReference(
        requestedUnit,
        nextUnits
      );

    const savedNotice =
      exists
        ? (
            isFR
              ? `${savedReference} enregistré dans la Structure du livre.`
              : `${savedReference} saved to Book Structure.`
          )
        : (
            isFR
              ? `${savedReference} ajouté à la Structure du livre.`
              : `${savedReference} added to Book Structure.`
          );

    setStructuralReflowError("");

    /*
     * Existing saved pages use one measured proposal.
     * Any required continuation page is an automatic
     * consequence of the writer's layout choice.
     */
    if (
      exists &&
      typeof onApplyStructuralManuscriptTransition ===
        "function"
    ) {
      let transitionResult;

      try {
        setStructuralReflowApplying(
          true
        );

        transitionResult =
          await onApplyStructuralManuscriptTransition({
            changedUnitId:
              requestedUnit.id,

            nextUnit:
              requestedUnit,

            /*
             * Browser-local concurrency authority only.
             * The parent must never send this callback
             * to the backend proposal endpoint.
             */
            isRequestCurrent:
              () =>
                latestEditingUnitSignatureRef
                  .current ===
                requestUnitSignature,
          });
      } catch (error) {
        setStructuralReflowError(
          String(
            error?.message ||
            (
              isFR
                ? "Le déplacement du manuscrit n’a pas pu être vérifié."
                : "The manuscript reflow could not be checked."
            )
          )
        );

        return;
      } finally {
        setStructuralReflowApplying(
          false
        );
      }

      /*
       * applicable:false returns to the ordinary local
       * save path, so repeat the child-local currency
       * check here as well.
       */
      if (
        latestEditingUnitSignatureRef
          .current !==
        requestUnitSignature
      ) {
        setStructuralReflowError(
          isFR
            ? "Cette page a changé pendant la vérification. Vos modifications les plus récentes ont été conservées. Enregistrez de nouveau les modifications de la page."
            : "This page changed while it was being checked. Your newer edits were kept. Save Page Changes again."
        );

        return;
      }

      if (
        !transitionResult
      ) {
        setStructuralReflowError(
          isFR
            ? "Le déplacement du manuscrit n’a pas pu être vérifié. Aucune modification n’a été appliquée."
            : "The manuscript reflow could not be checked. No changes were applied."
        );

        return;
      }

      if (
        transitionResult.ok ===
          false
      ) {
        setStructuralReflowError(
          transitionResult.error ||
          (
            isFR
              ? "Le déplacement du manuscrit n’est pas prêt. Aucune modification n’a été appliquée."
              : "The manuscript reflow is not ready. No changes were applied."
          )
        );

        return;
      }

      /*
       * Confirmation is no longer part of the writer
       * workflow. Fail closed if an older contract ever
       * attempts to recreate it.
       */
      if (
        transitionResult
          .confirmationRequired ===
            true
      ) {
        setStructuralReflowError(
          isFR
            ? "La redistribution automatique du manuscrit a demandé une confirmation inattendue. Aucune modification n’a été appliquée."
            : "Automatic manuscript reflow unexpectedly requested confirmation. No changes were applied."
        );

        return;
      }

      if (
        transitionResult
          .applicable ===
            true
      ) {
        if (
          transitionResult
            .applied ===
              true &&
          transitionResult
            .readyToApply ===
              true
        ) {
          setSavedUnitNotice(
            isFR
              ? `${savedReference} enregistré dans la Structure du livre. Les pages de continuation nécessaires ont été ajoutées automatiquement.`
              : `${savedReference} saved to Book Structure. Any needed continuation pages were added automatically.`
          );

          setEditingUnit(
            null
          );

          return;
        }

        setStructuralReflowError(
          transitionResult.error ||
          (
            isFR
              ? "Le manuscrit n’a pas pu être redistribué en toute sécurité. Aucune modification n’a été appliquée."
              : "The manuscript could not be reflowed safely. No changes were applied."
          )
        );

        return;
      }

      /*
       * applicable:false means this edit does not require
       * measured continuation insertion. Preserve the
       * normal Book Structure save path.
       */
    }

    onChange(
      "units",
      nextUnits
    );

    setSavedUnitNotice(
      savedNotice
    );

    setEditingUnit(
      null
    );
  }

  function requestRemoveUnit(
    id
  ) {
    setPendingRemovalId(
      id
    );
  }

  function cancelRemoveUnit() {
    setPendingRemovalId(
      null
    );
  }

  function confirmRemoveUnit(
    event
  ) {
    event.preventDefault();

    if (!pendingRemovalId) {
      return;
    }

    onChange(
      "units",
      units.filter(
        (unit) =>
          unit.id !==
          pendingRemovalId
      )
    );

    if (
      editingUnit?.id ===
      pendingRemovalId
    ) {
      setEditingUnit(
        null
      );
    }

    setPendingRemovalId(
      null
    );
  }

  function duplicateUnit(unit) {
    onChange("units", [
      ...units,
      {
        ...unit,
        id: `unit-${Date.now()}-${units.length + 1}`,
        workingTitle: unit.workingTitle
          ? `${unit.workingTitle} ${isFR ? "(copie)" : "(copy)"}`
          : "",
        pageNumber: "",
      },
    ]);
  }

  function moveUnit(index, direction) {
    const target = index + direction;
    if (target < 0 || target >= units.length) return;

    const nextUnits = [...units];
    const [moved] = nextUnits.splice(index, 1);
    nextUnits.splice(target, 0, moved);
    onChange("units", nextUnits);
  }

  function moveUnitToPosition(
    unitId,
    requestedPosition
  ) {
    const sourceIndex =
      units.findIndex(
        (unit) =>
          String(
            unit?.id || ""
          ).trim() ===
          String(
            unitId || ""
          ).trim()
      );

    const position =
      Number.parseInt(
        String(
          requestedPosition || ""
        ),
        10
      );

    if (
      sourceIndex < 0 ||
      !Number.isInteger(position) ||
      position < 1 ||
      position > units.length
    ) {
      return false;
    }

    const targetIndex =
      position - 1;

    if (
      sourceIndex !==
      targetIndex
    ) {
      const nextUnits = [
        ...units,
      ];

      const [moved] =
        nextUnits.splice(
          sourceIndex,
          1
        );

      nextUnits.splice(
        targetIndex,
        0,
        moved
      );

      onChange(
        "units",
        nextUnits
      );
    }

    setBookStructureRangeStart(
      Math.floor(
        targetIndex /
          BOOK_STRUCTURE_RANGE_SIZE
      ) *
        BOOK_STRUCTURE_RANGE_SIZE
    );

    return true;
  }

  function submitMoveToPosition(
    event,
    unitId
  ) {
    event.preventDefault();

    if (
      !moveUnitToPosition(
        unitId,
        movePositionValue
      )
    ) {
      return;
    }

    setMovingUnitId("");
    setMovePositionValue("");
  }

  function beginBookStructureDrag(
    event,
    unitId
  ) {
    const resolvedUnitId =
      String(
        unitId || ""
      ).trim();

    if (!resolvedUnitId) {
      return;
    }

    setDraggingUnitId(
      resolvedUnitId
    );

    setDragOverUnitId("");

    if (
      event?.dataTransfer
    ) {
      event.dataTransfer.effectAllowed =
        "move";

      event.dataTransfer.setData(
        "text/plain",
        resolvedUnitId
      );
    }
  }

  function finishBookStructureDrag() {
    setDraggingUnitId("");
    setDragOverUnitId("");
  }

  function dropBookStructureUnit(
    event,
    targetIndex
  ) {
    event.preventDefault();

    const transferredUnitId =
      String(
        event
          ?.dataTransfer
          ?.getData(
            "text/plain"
          ) ||
          draggingUnitId ||
          ""
      ).trim();

    if (
      transferredUnitId
    ) {
      moveUnitToPosition(
        transferredUnitId,
        targetIndex + 1
      );
    }

    finishBookStructureDrag();
  }

  const summary = useMemo(() => {
    const spreads = units.filter(
      (unit) =>
        unit.unitType === "double_page_spread"
    ).length;

    const illustrations = units.filter(
      (unit) =>
        unit.illustrationPlan !== "none"
    ).length;

    const incomplete = units.filter(
      (unit) =>
        unit.status === "not_planned" ||
        !String(unit.purpose || "").trim()
    ).length;

    const estimatedPages =
      childrensBookTotalPhysicalPages(
        units
      );

    return {
      spreads,
      illustrations,
      incomplete,
      estimatedPages,
    };
  }, [units]);

  const inheritedRows = [
    [isFR ? "Titre du livre" : "Book title", projectPlan.identity.title],
    [isFR ? "Tranche d’âge" : "Age range", projectPlan.audience.ageRange],
    [isFR ? "Niveau de lecture" : "Reading level", projectPlan.audience.readingLevel],
    [isFR ? "Forme du livre" : "Book form", projectPlan.bookForm.type],
    [isFR ? "Format ou dimensions" : "Trim size or dimensions", projectPlan.visualFormat.trimSize],
    [isFR ? "Format de publication" : "Publication format", projectPlan.visualFormat.publicationFormat],
  ];

  function renderBookStructureRangeNavigation() {
    if (
      units.length <=
      BOOK_STRUCTURE_RANGE_SIZE
    ) {
      return null;
    }

    const endIndex =
      Math.min(
        units.length,
        boundedBookStructureRangeStart +
          BOOK_STRUCTURE_RANGE_SIZE
      ) - 1;

    const firstItem =
      physicalPageItems[
        boundedBookStructureRangeStart
      ];

    const lastItem =
      physicalPageItems[
        endIndex
      ];

    const finalItem =
      physicalPageItems[
        physicalPageItems.length - 1
      ];

    const pageStart =
      Number(firstItem?.pageStart) || 0;

    const pageEnd =
      Number(lastItem?.pageEnd) || 0;

    const totalPages =
      Number(finalItem?.pageEnd) || 0;

    return (
      <section
        className="ns-card ns-nbb-major-panel"
        aria-label={
          isFR
            ? "Navigation de la Structure du livre"
            : "Book Structure navigation"
        }
        style={{
          padding: 16,
          borderRadius: 16,
          marginBottom: 16,
          display: "grid",
          gap: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <strong>
            {isFR
              ? `Pages ${pageStart}–${pageEnd} sur ${totalPages}`
              : `Pages ${pageStart}–${pageEnd} of ${totalPages}`}
          </strong>

          <span className="ns-card-meta">
            {isFR
              ? "Un maximum de 20 cartes est affiché à la fois."
              : "A maximum of 20 page or spread cards is shown at a time."}
          </span>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "auto minmax(220px, 1fr) auto",
            gap: 10,
            alignItems: "end",
          }}
        >
          <button
            className="ns-button ns-button-outline"
            type="button"
            disabled={
              boundedBookStructureRangeStart === 0
            }
            onClick={() =>
              setBookStructureRangeStart(
                Math.max(
                  0,
                  boundedBookStructureRangeStart -
                    BOOK_STRUCTURE_RANGE_SIZE
                )
              )
            }
          >
            {isFR
              ? "20 précédentes"
              : "Previous 20"}
          </button>

          <label
            style={{
              display: "grid",
              gap: 6,
              fontWeight: 800,
            }}
          >
            <span>
              {isFR
                ? "Aller à une page ou double page"
                : "Jump to Page or Spread"}
            </span>

            <select
              aria-label={
                isFR
                  ? "Aller à une page ou double page"
                  : "Jump to Page or Spread"
              }
              value=""
              onChange={(event) => {
                const unitId =
                  String(
                    event.target.value || ""
                  ).trim();

                const index =
                  units.findIndex(
                    (unit) =>
                      String(
                        unit?.id || ""
                      ).trim() ===
                      unitId
                  );

                if (index < 0) {
                  return;
                }

                setBookStructureRangeStart(
                  Math.floor(
                    index /
                      BOOK_STRUCTURE_RANGE_SIZE
                  ) *
                    BOOK_STRUCTURE_RANGE_SIZE
                );
              }}
              style={{
                minHeight: 44,
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid #cbd5e1",
                background: "#ffffff",
                font: "inherit",
              }}
            >
              <option value="">
                {isFR
                  ? "Choisir une page ou double page"
                  : "Choose a page or spread"}
              </option>

              {units.map(
                (unit, index) => {
                  const label =
                    childrensBookWriterPageLabel(
                      physicalPageItems[
                        index
                      ]
                    );

                  const title =
                    String(
                      unit?.workingTitle || ""
                    ).trim();

                  return (
                    <option
                      key={
                        unit?.id ||
                        String(index)
                      }
                      value={
                        unit?.id || ""
                      }
                    >
                      {label}
                      {title
                        ? ` · ${title}`
                        : ""}
                    </option>
                  );
                }
              )}
            </select>
          </label>

          <button
            className="ns-button ns-button-outline"
            type="button"
            disabled={
              boundedBookStructureRangeStart >=
              (
                bookStructureRangeCount - 1
              ) *
                BOOK_STRUCTURE_RANGE_SIZE
            }
            onClick={() =>
              setBookStructureRangeStart(
                Math.min(
                  (
                    bookStructureRangeCount - 1
                  ) *
                    BOOK_STRUCTURE_RANGE_SIZE,
                  boundedBookStructureRangeStart +
                    BOOK_STRUCTURE_RANGE_SIZE
                )
              )
            }
          >
            {isFR
              ? "20 suivantes"
              : "Next 20"}
          </button>
        </div>
      </section>
    );
  }

  if (reviewingMap) {
    return (
      <section aria-label={isFR ? "Vérification du plan des pages" : "Review page and spread plan"}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16, marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: "0.12em", textTransform: "uppercase", color: "#b45309" }}>
              {isFR ? "NextScenes Book Blueprint · Plan du livre" : "NextScenes Book Blueprint · Book Structure"}
            </div>
            <h2 className="ns-section-title" style={{ margin: "5px 0 8px" }}>
              {isFR ? "Vérifiez les pages et doubles pages" : "Review Pages and Spreads"}
            </h2>
            <p className="ns-card-meta" style={{ margin: 0 }}>
              {isFR
                ? "Cette estimation sert uniquement à organiser le livre. La pagination finale pourra changer pendant la mise en page."
                : "This estimate is only for organising the book. Final pagination may change during layout and print preparation."}
            </p>
          </div>
          <button className="ns-button ns-button-outline" type="button" onClick={onExit}>
            {isFR ? "Retour à Mes projets" : "Return to My Projects"}
          </button>
        </div>

        <div className="ns-nbb-card-grid ns-nbb-summary-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 18 }}>
          {[
            [isFR ? "Pages estimées" : "Estimated pages", summary.estimatedPages],
            [isFR ? "Doubles pages" : "Two-page spreads", summary.spreads],
            [isFR ? "Illustrations prévues" : "Planned illustrations", summary.illustrations],
            [isFR ? "Éléments incomplets" : "Incomplete units", summary.incomplete],
          ].map(([label, value]) => (
            <div key={label} className="ns-card ns-nbb-summary-card" style={{ padding: 16, borderRadius: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 900, color: "#64748b" }}>{label}</div>
              <div style={{ marginTop: 5, fontSize: 24, fontWeight: 900, color: "#1e293b" }}>{value}</div>
            </div>
          ))}
        </div>

          {renderBookStructureRangeNavigation()}

        <div className="ns-nbb-major-grid" style={{ display: "grid", gap: 14 }}>
          {units.length === 0 ? (
            <div className="ns-card ns-nbb-major-panel" style={{ padding: 20, borderRadius: 16 }}>
              {isFR ? "Aucune page ou double page n’a encore été ajoutée." : "No page or spread has been added yet."}
            </div>
          ) : (
            visibleBookStructureRows.map(({ unit, index }) => (
              <article key={unit.id} data-testid="book-structure-unit-card" className="ns-card ns-nbb-major-panel" style={{ padding: 18, borderRadius: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div>
                    <strong>
                      {childrensBookWriterPageLabel(
                        physicalPageItems[
                          index
                        ]
                      )}{unit.workingTitle ? " · " : ""}
                      {unit.workingTitle || ""}
                    </strong>
                    <p className="ns-card-meta" style={{ margin: "6px 0 0" }}>
                      {labelFor(statusOptions, unit.status)}
                    </p>

                    {renderUnitAttention(
                      unit
                    )}

                    <ChildrensBookIllustrationAssetPanel
                      compact
                      lang={lang}
                      unit={unit}
                      childrensBookAssets={
                        childrensBookAssets
                      }
                      onLoadChildrensBookAssetFile={
                        onLoadChildrensBookAssetFile
                      }
                    />
                  </div>
                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    onClick={() => {
                      setSavedUnitNotice("");
                      setEditingUnit(unit);
                      setReviewingMap(false);
                    }}
                  >
                    {isFR ? "Modifier" : "Edit"}
                  </button>
                </div>
                <p style={{ lineHeight: 1.6, marginBottom: 0 }}>
                  {displayValue(unit.purpose)}
                </p>
              </article>
            ))
          )}
        </div>

        <div className="ns-card" style={{ padding: 16, marginTop: 18, borderRadius: 16, background: "#fff7ed", border: "1px solid rgba(234,88,12,0.25)" }}>
          <strong>
            {persistenceEnabled
              ? (
                  isFR
                    ? "Enregistrement du Plan du livre"
                    : "Saving the Book Structure"
                )
              : (
                  isFR
                    ? "Brouillon local uniquement"
                    : "Local draft only"
                )}
          </strong>

          <p className="ns-card-meta" style={{ margin: "7px 0 0", lineHeight: 1.55 }}>
            {persistenceEnabled
              ? (
                  isFR
                    ? "Utilisez « Enregistrer le Blueprint » en haut de la page pour conserver les modifications du Plan du livre."
                    : "Use “Save Blueprint” at the top of the page to preserve Book Structure changes."
                )
              : (
                  isFR
                    ? "Ce plan existe uniquement dans la mémoire locale de cette page et disparaîtra si vous l’actualisez ou la quittez."
                    : "This map exists only in this page’s local memory and will disappear if you refresh or leave the page."
                )}
          </p>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginTop: 18 }}>
          <button className="ns-button ns-button-outline" type="button" onClick={() => setReviewingMap(false)}>
            {isFR ? "Modifier le plan des pages" : "Edit Page and Spread Plan"}
          </button>
          <button
            className="ns-button ns-button-primary"
            type="button"
            disabled
            title={
              persistenceEnabled
                ? (
                    isFR
                      ? "Utilisez le bouton Enregistrer le Blueprint en haut de la page."
                      : "Use the Save Blueprint button at the top of the page."
                  )
                : (
                    isFR
                      ? "L’enregistrement permanent n’est pas activé."
                      : "Persistent saving is not enabled."
                  )
            }
          >
            {persistenceEnabled
              ? (
                  isFR
                    ? "Enregistrer avec le Blueprint"
                    : "Save with Blueprint"
                )
              : (
                  isFR
                    ? "Enregistrement non activé"
                    : "Saving not enabled"
                )}
          </button>
        </div>
      </section>
    );
  }

  if (editingUnit) {
    return (
      <section aria-label={isFR ? "Modifier une page ou une double page" : "Edit a page or spread"}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16, marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: "0.12em", textTransform: "uppercase", color: "#b45309" }}>
              {isFR ? "Planification guidée" : "Guided planning"}
            </div>
            <h2 className="ns-section-title" style={{ margin: "5px 0 8px" }}>
              {isFR ? "Décrivez cette partie du livre" : "Describe This Part of the Book"}
            </h2>
            <p className="ns-card-meta" style={{ margin: 0 }}>
              {isFR
                ? "Chaque champ explique le type d’information attendu. Vous pourrez corriger le plan plus tard."
                : "Each field explains what kind of information belongs there. You can revise the plan later."}
            </p>
          </div>
          <button className="ns-button ns-button-outline" type="button" onClick={onExit}>
            {isFR ? "Retour à Mes projets" : "Return to My Projects"}
          </button>
        </div>

        {editingExistingUnit ? (
          <div
            role="status"
            style={{
              padding: "12px 14px",
              marginBottom: 16,
              borderRadius: 12,
              border: hasUnsavedUnitChanges
                ? "1px solid rgba(217,119,6,0.42)"
                : "1px solid rgba(100,116,139,0.24)",
              background: hasUnsavedUnitChanges
                ? "#fff7ed"
                : "#f8fafc",
              color: hasUnsavedUnitChanges
                ? "#9a3412"
                : "#475569",
              fontWeight: 800,
              lineHeight: 1.5,
            }}
          >
            {hasUnsavedUnitChanges
              ? (
                  isFR
                    ? `Modification de ${unitReference(editingUnit)} · Modifications pas encore enregistrées dans la Structure du livre.`
                    : `Editing ${unitReference(editingUnit)} · Changes not yet saved to Book Structure.`
                )
              : (
                  isFR
                    ? `Modification de ${unitReference(editingUnit)} · Effectuez vos modifications, puis utilisez « ${unitSaveActionLabel(editingUnit)} ».`
                    : `Editing ${unitReference(editingUnit)} · Make your changes, then use “${unitSaveActionLabel(editingUnit)}”.`
                )}
          </div>
        ) : null}

        <div className="ns-card ns-nbb-form-panel" style={{ padding: "clamp(18px, 3vw, 26px)", borderRadius: 20, display: "grid", gap: 18 }}>

          <div
            data-testid="book-structure-page-setup-heading"
          >
            <strong
              style={{
                display: "block",
                fontSize: 19,
                color: "#1e293b",
              }}
            >
              {isFR
                ? "1. Configuration de la page"
                : "1. Page Setup"}
            </strong>

            <p
              className="ns-card-meta"
              style={{
                margin: "6px 0 0",
                lineHeight: 1.55,
              }}
            >
              {isFR
                ? "Commencez par les quelques choix dont NextScenes a besoin pour comprendre cette page. Les choix obligatoires et facultatifs sont indiqués clairement."
                : "Start with the few choices NextScenes needs to understand this page. Required and optional choices are marked clearly."}
            </p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
            <SelectField
              label={isFR ? "Pages utilisées · Obligatoire" : "Pages used · Required"}
              help={isFR ? "Choisissez si cette partie utilise une page, deux pages en vis-à-vis ou une page spéciale." : "Choose whether this part uses one page, two facing pages, or a special page."}
              value={editingUnit.unitType}
              onChange={(value) => setEditingUnit((current) => ({ ...current, unitType: value }))}
              options={unitTypeOptions}
            />

            <div
              data-testid="book-structure-physical-page-reference"
              className="ns-nbb-inner-panel"
              style={{
                padding: 12,
                borderRadius: 12,
                background: "#f8fafc",
                border:
                  "1px solid #cbd5e1",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 900,
                  color: "#64748b",
                }}
              >
                {isFR
                  ? "Pages du livre"
                  : "Book pages"}
              </div>

              <strong
                style={{
                  display: "block",
                  marginTop: 5,
                }}
              >
                {unitReference(
                  editingUnit
                )}
              </strong>

              <p
                className="ns-card-meta"
                style={{
                  margin: "7px 0 0",
                  lineHeight: 1.5,
                }}
              >
                {isFR
                  ? "Calculées automatiquement à partir de la Structure du livre actuelle. Ces numéros peuvent changer si des pages ou doubles pages sont ajoutées, supprimées, déplacées ou modifiées."
                  : "Calculated automatically from the current Book Structure. These page numbers may change when pages or spreads are added, removed, moved, or changed."}
              </p>
            </div>

            <Field
              label={isFR ? "Titre de travail · Facultatif" : "Working title · Optional"}
              help={isFR ? "Donnez un nom court qui vous aidera à reconnaître cette partie." : "Give this part a short name that will help you recognise it later."}
              placeholder={isFR ? "Exemple : Ugo découvre la graine" : "Example: Ugo Finds the Seed"}
              value={editingUnit.workingTitle}
              onChange={(value) => setEditingUnit((current) => ({ ...current, workingTitle: value }))}
            />

          </div>

        </div>

        <div
          className="ns-card ns-nbb-form-panel"
          data-testid="book-structure-manuscript-drafting"
          style={{
            padding:
              "clamp(18px, 3vw, 26px)",
            borderRadius: 20,
            display: "grid",
            gap: 14,
            marginTop: 18,
          }}
        >
          <div
            data-testid="book-structure-writing-heading"
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#64748b",
                marginBottom: 6,
              }}
            >
              {isFR
                ? "2. Écrire la page"
                : "2. Write the Page"}
            </div>
          </div>

            <SelectField
              label={isFR ? "Quantité approximative de texte · Obligatoire" : "Approximate text amount · Required"}
              help={isFR ? "Il s’agit seulement d’une estimation pour équilibrer le texte et les images." : "This is only an estimate for balancing words and pictures."}
              value={editingUnit.textAmount}
              onChange={(value) => setEditingUnit((current) => ({ ...current, textAmount: value }))}
              options={textAmountOptions}
            />


          <div>
            <strong
              style={{
                display: "block",
                fontSize: 18,
                color: "#1e293b",
              }}
            >
              {isFR
                ? "Rédiger le texte de cette page"
                : "Draft This Page"}
            </strong>

            <p
              className="ns-card-meta"
              style={{
                margin: "6px 0 0",
                lineHeight: 1.55,
              }}
            >
              {isFR
                ? "Écrivez ici le texte que le jeune lecteur verra ou entendra. Il s’agit du même manuscrit que dans l’espace Manuscrit — aucune copie ni synchronisation séparée."
                : "Write here the words the young reader will see or hear. This is the same manuscript used in the Manuscript workspace — there is no separate copy or sync step."}
            </p>
          </div>

          {!editingExistingUnit ? (
            <div
              role="note"
              data-testid="book-structure-manuscript-save-first"
              style={{
                padding: 12,
                borderRadius: 12,
                border:
                  "1px solid rgba(37,99,235,0.22)",
                background:
                  "#eff6ff",
                lineHeight: 1.55,
              }}
            >
              {isFR
                ? "Ajoutez d’abord cette page ou double page à la Structure du livre. Vous pourrez ensuite la rouvrir et rédiger son manuscrit ici."
                : "Add this page or spread to Book Structure first. Then reopen it to draft its manuscript here."}
            </div>
          ) : hasUnsavedUnitChanges ? (
            <div
              role="note"
              data-testid="book-structure-manuscript-save-structure-first"
              style={{
                padding: 12,
                borderRadius: 12,
                border:
                  "1px solid rgba(217,119,6,0.32)",
                background:
                  "#fff7ed",
                color:
                  "#9a3412",
                lineHeight: 1.55,
                fontWeight: 700,
              }}
            >
              {isFR
                ? "Enregistrez d’abord les modifications de cette page. Le flux du manuscrit doit utiliser la Structure du livre réellement enregistrée."
                : "Save this page’s structural changes first. Manuscript flow must use the Book Structure that is actually saved."}
            </div>
          ) : editingUnitUsesNoText ? (
            <div
              role="note"
              data-testid="book-structure-manuscript-no-text"
              style={{
                padding: 12,
                borderRadius: 12,
                border:
                  "1px solid rgba(100,116,139,0.24)",
                background:
                  "#f8fafc",
                lineHeight: 1.55,
              }}
            >
              {isFR
                ? "Cette page est définie sur « Aucun texte ». Modifiez ce réglage et enregistrez la page si vous souhaitez y rédiger du texte."
                : "This page is set to “No text.” Change that setting and save the page if you want to draft manuscript text here."}
            </div>
          ) : !manuscriptEditingEnabled ? (
            <div
              role="note"
              data-testid="book-structure-manuscript-unavailable"
              style={{
                padding: 12,
                borderRadius: 12,
                border:
                  "1px solid rgba(100,116,139,0.24)",
                background:
                  "#f8fafc",
                lineHeight: 1.55,
              }}
            >
              {isFR
                ? "La rédaction du manuscrit n’est pas disponible dans cet état du projet."
                : "Manuscript drafting is not available in this project state."}
            </div>
          ) : manuscriptDraftingAvailable ? (
            <ChildrensBookManuscriptEditor
              key={
                `book-structure-manuscript-${
                  editingUnit.id
                }`
              }
              lang={lang}
              entry={
                manuscriptEntryFor(
                  editingUnit.id
                )
              }
              selectionRestore={
                String(
                  manuscriptSelectionRestore
                    ?.unitId ||
                  ""
                ).trim() ===
                  String(
                    editingUnit.id ||
                    ""
                  ).trim()
                  ? manuscriptSelectionRestore
                  : null
              }
              onChange={(fields) =>
                handleBookStructureManuscriptChange(
                  editingUnit.id,
                  fields
                )
              }
            />
          ) : null}
        </div>


        <div
          className="ns-card ns-nbb-form-panel"
          data-testid="book-structure-picture-planning"
          style={{
            padding:
              "clamp(18px, 3vw, 26px)",
            borderRadius: 20,
            display: "grid",
            gap: 14,
            marginTop: 18,
          }}
        >
          <div>
            <strong
              style={{
                display: "block",
                fontSize: 18,
                color: "#1e293b",
              }}
            >
              {isFR
                ? "3. Illustration"
                : "3. Picture"}
            </strong>

            <p
              className="ns-card-meta"
              style={{
                margin: "6px 0 0",
                lineHeight: 1.55,
              }}
            >
              {isFR
                ? "Cette partie concerne uniquement l’image de cette page. La planification détaillée reste disponible dans l’espace Illustrations."
                : "This section is only about the picture for this page. Detailed illustration planning remains available in the Illustrations workspace."}
            </p>
          </div>

            <SelectField
              label={isFR ? "Place de l’illustration · Obligatoire" : "Illustration requirement · Required"}
              help={isFR ? "Choisissez l’espace approximatif que l’image devrait occuper." : "Choose roughly how much space the picture should occupy."}
              value={editingUnit.illustrationPlan}
              onChange={(value) =>
                setEditingUnit(
                  (current) =>
                    childrensBookIllustrationPlanChangeForUnit(
                      current,
                      value
                    )
                )
              }
              options={editingIllustrationOptions}
            />

            {childrensBookFullSpreadUnitConflict(
              editingUnit
            ) ? (
              <div
                role="alert"
                data-testid="book-structure-full-spread-unit-conflict"
                style={{
                  gridColumn:
                    "1 / -1",
                  padding: 12,
                  borderRadius: 12,
                  border:
                    "1px solid rgba(217,119,6,0.32)",
                  background:
                    "#fffbeb",
                  color:
                    "#92400e",
                  lineHeight:
                    1.55,
                }}
              >
                <strong>
                  {isFR
                    ? "Cette option nécessite une double page."
                    : "This option requires a Two-page spread."}
                </strong>{" "}

                {isFR
                  ? "Choisissez une option d’illustration compatible avec cette page, ou changez le type d’élément en « Double page ». Votre choix actuel n’a pas été modifié automatiquement."
                  : "Choose an illustration option compatible with this page, or change the Planning unit to Two-page spread. Your existing choice has not been changed automatically."}
              </div>
            ) : null}

            {editingUnit.unitType ===
              "double_page_spread" &&
              [
                "small_illustration",
                "half_page",
                "full_page",
              ].includes(
                editingUnit
                  .illustrationPlan
              ) && (
                <SelectField
                  label={
                    isFR
                      ? "Page de l’image · Obligatoire"
                      : "Picture page · Required"
                  }
                  help={
                    editingUnit
                      .illustrationPlan ===
                    "full_page"
                      ? (
                          isFR
                            ? "Choisissez si l’illustration pleine page apparaît sur la page gauche ou droite de cette double page."
                            : "Choose whether the full-page picture appears on the left or right page of this spread."
                        )
                      : (
                          isFR
                            ? "Choisissez si l’image partage la page du texte ou apparaît sur la page gauche ou droite de cette double page."
                            : "Choose whether the picture shares the text page or appears on the left or right page of this spread."
                        )
                  }
                  value={
                    editingUnit
                      .illustrationPageSide ||
                    ""
                  }
                  onChange={(value) =>
                    setEditingUnit(
                      (current) => ({
                        ...current,
                        illustrationPageSide:
                          value,
                      })
                    )
                  }
                  options={[
                    {
                      value: "",
                      label: isFR
                        ? "Choisir la page de l’image"
                        : "Choose picture page",
                    },
                    ...(
                      editingUnit
                        .illustrationPlan ===
                      "full_page"
                        ? []
                        : [
                            {
                              value:
                                "with_text",
                              label:
                                isFR
                                  ? "Avec le texte"
                                  : "With the text",
                            },
                          ]
                    ),
                    {
                      value: "left",
                      label:
                        picturePageOptionLabel(
                          "left",
                          editingUnit
                        ),
                    },
                    {
                      value: "right",
                      label:
                        picturePageOptionLabel(
                          "right",
                          editingUnit
                        ),
                    },
                  ]}
                />
              )}


          {String(
            editingUnit
              .illustrationPlan ||
            ""
          )
            .trim()
            .toLowerCase() ===
          "none" ? (
            <div
              role="note"
              style={{
                padding: 12,
                borderRadius: 12,
                border:
                  "1px solid rgba(100,116,139,0.24)",
                background:
                  "#f8fafc",
                lineHeight: 1.55,
              }}
            >
              {isFR
                ? "Aucune illustration n’est prévue pour cette page."
                : "No illustration is planned for this page."}
            </div>
          ) : (
          <>
            <Field
              multiline
              label={isFR ? "Que doit montrer l’illustration ? (facultatif)" : "What should the illustration show? (optional)"}
              help={isFR ? "Mentionnez les personnages, le décor, l’action, l’humeur, les objets importants et les détails culturels." : "Mention characters, setting, action, mood, important objects, and cultural details."}
              placeholder={isFR ? "Exemple : Ugo agenouillée près de la graine, jardin vert, lumière douce, expression curieuse." : "Example: Ugo kneeling beside the seed, green garden, gentle light, curious expression."}
              value={editingUnit.illustrationNote}
              onChange={(value) => setEditingUnit((current) => ({ ...current, illustrationNote: value }))}
            />

            {!editingExistingUnit ? (
              <div
                role="note"
                data-testid="book-structure-illustration-save-first"
                style={{
                  padding: 12,
                  borderRadius: 12,
                  border:
                    "1px solid rgba(37,99,235,0.22)",
                  background:
                    "#eff6ff",
                  lineHeight: 1.55,
                }}
              >
                {isFR
                  ? "Ajoutez d’abord cette page ou double page à la Structure du livre. Vous pourrez ensuite ajouter son illustration ici."
                  : "Add this page or spread to Book Structure first. Then you can add its illustration here."}
              </div>
            ) : (
              <>
                {hasUnsavedUnitChanges ? (
                  <div
                    role="note"
                    data-testid="book-structure-illustration-save-structure-first"
                    style={{
                      padding: 12,
                      borderRadius: 12,
                      border:
                        "1px solid rgba(217,119,6,0.32)",
                      background:
                        "#fff7ed",
                      color:
                        "#9a3412",
                      lineHeight: 1.55,
                      fontWeight: 700,
                    }}
                  >
                    {isFR
                      ? "Enregistrez d’abord les modifications structurelles de cette page. L’illustration existante peut rester visible, mais son ajout ou sa gestion doit utiliser la Structure du livre réellement enregistrée."
                      : "Save this page’s structural changes first. Its existing illustration may remain visible, but adding or managing an image must use the Book Structure that is actually saved."}
                  </div>
                ) : !assetManagementEnabled ? (
                  <div
                    role="note"
                    data-testid="book-structure-illustration-save-project-first"
                    style={{
                      padding: 12,
                      borderRadius: 12,
                      border:
                        "1px solid rgba(100,116,139,0.24)",
                      background:
                        "#f8fafc",
                      lineHeight: 1.55,
                    }}
                  >
                    {isFR
                      ? "Enregistrez d’abord le projet pour ajouter ou gérer le fichier d’illustration."
                      : "Save the project first to add or manage the illustration file."}
                  </div>
                ) : null}

                {fullPageAuthoringSurfaceAvailable ? (
                  <div
                    data-testid="book-structure-full-page-authoring-surface"
                    style={{
                      width:
                        "min(100%, 520px)",
                      margin:
                        "0 auto 16px",
                    }}
                  >
                    <ChildrensBookAuthoringImageSurface
                      geometry={
                        fullPageAuthoringSurfaceGeometry
                      }
                      containerAspectRatio={
                        authoringPageAspectRatio
                      }
                      resizable
                      preserveAspectRatio
                      onGeometryCommit={
                        commitFullPageAuthoringGeometry
                      }
                      label={
                        isFR
                          ? "Composition de l’illustration pleine page"
                          : "Full-page illustration composition"
                      }
                    >
                      <ChildrensBookManuscriptIllustrationContext
                        lang={lang}
                        unit={
                          savedEditingUnit
                        }
                        childrensBookAssets={
                          childrensBookAssets
                        }
                        onLoadChildrensBookAssetFile={
                          onLoadChildrensBookAssetFile
                        }
                        presentation="authoring_surface"
                      />
                    </ChildrensBookAuthoringImageSurface>
                  </div>
                ) : null}

                <ChildrensBookIllustrationAssetPanel
                  lang={lang}
                  unit={savedEditingUnit}
                  assetManagementEnabled={
                    Boolean(
                      assetManagementEnabled &&
                      !hasUnsavedUnitChanges
                    )
                  }
                  managementControlVisible={
                    Boolean(
                      assetManagementEnabled &&
                      !hasUnsavedUnitChanges
                    )
                  }
                  childrensBookAssets={
                    childrensBookAssets
                  }
                  childrensBookAssetsLoading={
                    childrensBookAssetsLoading
                  }
                  childrensBookAssetsError={
                    childrensBookAssetsError
                  }
                  childrensBookAssetActionId={
                    childrensBookAssetActionId
                  }
                  onLoadChildrensBookAssetFile={
                    onLoadChildrensBookAssetFile
                  }
                  onUploadChildrensBookIllustration={
                    onUploadChildrensBookIllustration
                  }
                  onRemoveChildrensBookAsset={
                    onRemoveChildrensBookAsset
                  }
                />
              </>
            )}
          </>

          )}
        </div>


        <details
          className="ns-card ns-nbb-form-panel"
          data-testid="book-structure-more-page-options"
          style={{
            padding:
              "clamp(16px, 2.5vw, 22px)",
            borderRadius: 20,
            marginTop: 18,
          }}
        >
          <summary
            style={{
              cursor: "pointer",
              fontWeight: 900,
              color: "#334155",
              fontSize: 17,
            }}
          >
            {isFR
              ? "4. Plus d’options pour la page · Facultatif"
              : "4. More Page Options · Optional"}
          </summary>

          <p
            className="ns-card-meta"
            style={{
              margin: "8px 0 0",
              lineHeight: 1.55,
            }}
          >
            {isFR
              ? "Ouvrez cette section seulement si cette page a besoin de notes de planification, d’une disposition particulière ou de texte imprimé supplémentaire."
              : "Open this only when the page needs planning notes, a special words-and-picture arrangement, or additional printed page text."}
          </p>

          <div
            style={{
              display: "grid",
              gap: 18,
              marginTop: 18,
            }}
          >
            <SelectField
              label={isFR ? "État de préparation · Facultatif" : "Planning status · Optional"}
              help={isFR ? "Indiquez à quel point cette partie est prête pour la rédaction ou l’illustration." : "Show how ready this part is for writing or illustration."}
              value={editingUnit.status}
              onChange={(value) => setEditingUnit((current) => ({ ...current, status: value }))}
              options={statusOptions}
            />

          <Field
            multiline
            label={isFR ? "Que se passe-t-il ici ? (facultatif)" : "What happens here? (optional)"}
            help={isFR ? "Décrivez l’action, l’idée, l’émotion ou l’information que le jeune lecteur doit recevoir." : "Describe the action, idea, emotion, or information the young reader should receive."}
            placeholder={isFR ? "Exemple : Ugo trouve une graine brillante et hésite à la toucher." : "Example: Ugo finds a shining seed and hesitates before touching it."}
            value={editingUnit.purpose}
            onChange={(value) => setEditingUnit((current) => ({ ...current, purpose: value }))}
          />

          <Field
            multiline
            label={isFR ? "Comment disposer le texte et l’image ? (facultatif)" : "How should the words and picture be arranged? (optional)"}
            help={isFR ? "Expliquez simplement où vous imaginez le texte et l’illustration." : "Explain simply where you imagine the text and illustration."}
            placeholder={isFR ? "Exemple : Texte court à gauche, grande illustration à droite." : "Example: Short text on the left, large illustration on the right."}
            value={editingUnit.layoutIntention}
            onChange={(value) => setEditingUnit((current) => ({ ...current, layoutIntention: value }))}
          />

          <div
            className="ns-nbb-inner-panel"
            data-testid="book-structure-page-composition"
            style={{
              display: "grid",
              gap: 14,
              padding: 16,
              borderRadius: 14,
              border:
                "1px solid rgba(100,116,139,0.24)",
              background: "#f8fafc",
            }}
          >
            <div>
              <strong
                style={{
                  display: "block",
                  fontSize: 18,
                  color: "#1e293b",
                }}
              >
                {isFR
                  ? "Composition de la page (facultatif)"
                  : "Page Composition (optional)"}
              </strong>

              <p
                className="ns-card-meta"
                style={{
                  margin: "6px 0 0",
                  lineHeight: 1.55,
                }}
              >
                {isFR
                  ? "Ces mots sont destinés à apparaître sur la page du livre. Ils sont différents du titre de travail, qui sert uniquement à organiser le projet."
                  : "These words are intended to appear on the book page. They are different from the Working title, which is only for organising the project."}
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(240px, 1fr))",
                gap: 14,
              }}
            >
              <Field
                label={
                  isFR
                    ? "Titre imprimé de la page (facultatif)"
                    : "Printed page title (optional)"
                }
                help={
                  isFR
                    ? "Laissez vide si aucun titre ne doit apparaître sur cette page."
                    : "Leave blank when no title should appear on this page."
                }
                value={
                  editingUnit
                    .pageComposition
                    ?.title ||
                  ""
                }
                onChange={(value) =>
                  setEditingUnit(
                    (current) => ({
                      ...current,
                      pageComposition: {
                        ...(
                          current
                            .pageComposition &&
                          typeof current
                            .pageComposition ===
                            "object" &&
                          !Array.isArray(
                            current
                              .pageComposition
                          )
                            ? current
                                .pageComposition
                            : {}
                        ),
                        title: value,
                      },
                    })
                  )
                }
              />

              <Field
                label={
                  isFR
                    ? "Sous-titre imprimé (facultatif)"
                    : "Printed page subtitle (optional)"
                }
                help={
                  isFR
                    ? "Texte secondaire placé avec le titre de la page."
                    : "Secondary text that belongs with the page title."
                }
                value={
                  editingUnit
                    .pageComposition
                    ?.subtitle ||
                  ""
                }
                onChange={(value) =>
                  setEditingUnit(
                    (current) => ({
                      ...current,
                      pageComposition: {
                        ...(
                          current
                            .pageComposition &&
                          typeof current
                            .pageComposition ===
                            "object" &&
                          !Array.isArray(
                            current
                              .pageComposition
                          )
                            ? current
                                .pageComposition
                            : {}
                        ),
                        subtitle: value,
                      },
                    })
                  )
                }
              />

              <Field
                multiline
                label={
                  isFR
                    ? "Légende (facultatif)"
                    : "Caption (optional)"
                }
                help={
                  isFR
                    ? "Une courte légende destinée au lecteur, si nécessaire."
                    : "A short reader-facing caption, when needed."
                }
                value={
                  editingUnit
                    .pageComposition
                    ?.caption ||
                  ""
                }
                onChange={(value) =>
                  setEditingUnit(
                    (current) => ({
                      ...current,
                      pageComposition: {
                        ...(
                          current
                            .pageComposition &&
                          typeof current
                            .pageComposition ===
                            "object" &&
                          !Array.isArray(
                            current
                              .pageComposition
                          )
                            ? current
                                .pageComposition
                            : {}
                        ),
                        caption: value,
                      },
                    })
                  )
                }
              />

              <Field
                label={
                  isFR
                    ? "Crédit de page (facultatif)"
                    : "Credit line (optional)"
                }
                help={
                  isFR
                    ? "Par exemple : Illustration de… Ne l’utilisez que lorsqu’un crédit doit réellement apparaître sur cette page."
                    : "For example: Illustration by… Use this only when a credit should actually appear on this page."
                }
                value={
                  editingUnit
                    .pageComposition
                    ?.creditLine ||
                  ""
                }
                onChange={(value) =>
                  setEditingUnit(
                    (current) => ({
                      ...current,
                      pageComposition: {
                        ...(
                          current
                            .pageComposition &&
                          typeof current
                            .pageComposition ===
                            "object" &&
                          !Array.isArray(
                            current
                              .pageComposition
                          )
                            ? current
                                .pageComposition
                            : {}
                        ),
                        creditLine: value,
                      },
                    })
                  )
                }
              />
            </div>
          </div>
          </div>
        </details>

        {structuralReflowError ? (
          <div
            data-testid="book-structure-structural-reflow-error"
            role="alert"
            style={{
              padding: "12px 14px",
              marginTop: 18,
              borderRadius: 12,
              border:
                "1px solid rgba(153,27,27,0.32)",
              background: "#fef2f2",
              color: "#991b1b",
              fontWeight: 700,
              lineHeight: 1.55,
            }}
          >
            {structuralReflowError}
          </div>
        ) : null}


        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginTop: 18 }}>
          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={() => {
setStructuralReflowError(
                ""
              );
              setEditingUnit(
                null
              );
            }}
            disabled={
              structuralReflowApplying ||
              structuralManuscriptTransitionSaving
            }
          >
            {isFR ? "Annuler" : "Cancel"}
          </button>
          <button
            className="ns-button ns-button-primary"
            type="button"
            onClick={saveUnit}
            disabled={
              structuralReflowApplying ||
              structuralManuscriptTransitionSaving
            }
            style={
              editingExistingUnit &&
              hasUnsavedUnitChanges
                ? {
                    background: "#d97706",
                    borderColor: "#d97706",
                    color: "#ffffff",
                  }
                : undefined
            }
          >
            {editingExistingUnit
              ? unitSaveActionLabel(
                  editingUnit
                )
              : (
                  isFR
                    ? "Ajouter au plan"
                    : "Add to Book Structure"
                )}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section aria-label={isFR ? "Planification des pages et doubles pages" : "Page and spread planning"}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16, marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: "0.12em", textTransform: "uppercase", color: "#b45309" }}>
            {"NextScenes Book Blueprint"}
          </div>
          <h2 className="ns-section-title" style={{ margin: "5px 0 8px" }}>
            {isFR ? "Planifiez vos pages" : "Plan Your Pages"}
          </h2>
          <p className="ns-card-meta" style={{ margin: 0, maxWidth: 760, lineHeight: 1.6 }}>
            {isFR
              ? "Organisez le livre avant la rédaction complète. Une page contient un seul côté; une double page réunit les pages gauche et droite visibles ensemble."
              : "Organise the book before writing the full manuscript. A page is one side; a two-page spread is the left and right pages seen together."}
          </p>
        </div>
        <button className="ns-button ns-button-outline" type="button" onClick={onExit}>
          {isFR ? "Retour à Mes projets" : "Return to My Projects"}
        </button>
      </div>

      <div className="ns-card ns-nbb-major-panel" style={{ padding: 18, borderRadius: 16, marginBottom: 18 }}>
        <h3 className="ns-card-title" style={{ marginTop: 0 }}>
          {isFR ? "Informations déjà choisies" : "Information Already Chosen"}
        </h3>
        <dl style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 12, margin: 0 }}>
          {inheritedRows.map(([label, value]) => (
            <div key={label} className="ns-nbb-inner-panel" style={{ padding: 12, borderRadius: 12, background: "#f8fafc" }}>
              <dt style={{ fontSize: 12, fontWeight: 900, color: "#64748b" }}>{label}</dt>
              <dd style={{ margin: "5px 0 0", fontWeight: 700 }}>{displayValue(value)}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div
        className="ns-nbb-page-structure-choice-guidance"
        role="note"
        style={{
          marginBottom: 12,
          padding: "12px 14px",
          borderRadius: 12,
          border: "1px solid #dbe4ee",
          background: "#f8fafc",
          lineHeight: 1.55,
        }}
      >
        <strong>
          {isFR
            ? "Une page est recommandée pour l’écriture normale."
            : "Single Page is recommended for normal writing."}
        </strong>

        <div
          className="ns-card-meta"
          style={{ marginTop: 5 }}
        >
          {isFR
            ? "Ajoutez normalement le livre une page à la fois. Le texte peut ainsi circuler plus naturellement entre les pages."
            : "Normally build your book one page at a time. This keeps writing, illustrations, and automatic text flow easier to manage."}
        </div>

        <div
          className="ns-card-meta"
          style={{ marginTop: 5 }}
        >
          {isFR
            ? "La double page est facultative. Choisissez-la uniquement lorsque deux pages en vis-à-vis doivent former volontairement une seule composition — par exemple une illustration panoramique sur toute la double page ou une mise en page qui doit rester associée."
            : "A Two-Page Spread is optional. Choose it only when two facing pages are deliberately meant to work together — for example, a panoramic full-spread illustration or a composition that must stay paired."}
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
        <button
          className="ns-button ns-button-outline"
          type="button"
          aria-expanded={
            frontMatterChoicesOpen
          }
          onClick={() =>
            setFrontMatterChoicesOpen(
              (open) => !open
            )
          }
        >
          {isFR
            ? "Ajouter une page liminaire"
            : "Add Front Matter"}
        </button>

        <button className="ns-button ns-button-primary" type="button" onClick={() => createUnit("single_page")}>
          {isFR ? "Ajouter une page — Recommandé" : "Add Page — Recommended"}
        </button>

        <button className="ns-button ns-button-outline" type="button" onClick={() => createUnit("double_page_spread")}>
          {isFR ? "Ajouter une double page — Facultatif" : "Add Two-Page Spread — Optional"}
        </button>
      </div>

      {frontMatterChoicesOpen ? (
        <div
          data-testid="childrens-book-front-matter-chooser"
          className="ns-card"
          style={{
            padding: 16,
            margin:
              "-8px 0 18px",
            borderRadius: 14,
            border:
              "1px solid rgba(37,99,235,0.20)",
            background:
              "#f8fafc",
          }}
        >
          <strong>
            {isFR
              ? "Choisissez une page liminaire"
              : "Choose Front Matter"}
          </strong>

          <p
            className="ns-card-meta"
            style={{
              margin:
                "7px 0 12px",
              lineHeight: 1.55,
              maxWidth: 820,
            }}
          >
            {isFR
              ? "NextScenes créera une page liminaire ordinaire et modifiable. Enregistrez d’abord la page dans la Structure du livre, puis rédigez son contenu avec « Rédiger cette page »."
              : "NextScenes will create an ordinary editable Front Matter page. Save it to Book Structure first, then write its content with Draft This Page."}
          </p>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={() =>
                createFrontMatterUnit(
                  "title_page"
                )
              }
            >
              {isFR
                ? "Page de titre"
                : "Title Page"}
            </button>

            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={() =>
                createFrontMatterUnit(
                  "copyright_page"
                )
              }
            >
              {isFR
                ? "Droits d’auteur"
                : "Copyright"}
            </button>

            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={() =>
                createFrontMatterUnit(
                  "dedication_page"
                )
              }
            >
              {isFR
                ? "Dédicace"
                : "Dedication"}
            </button>

            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={() =>
                createFrontMatterUnit(
                  "contents"
                )
              }
            >
              {isFR
                ? "Sommaire"
                : "Contents"}
            </button>

            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={() =>
                createFrontMatterUnit(
                  "other"
                )
              }
            >
              {isFR
                ? "Autre page liminaire"
                : "Other Front Matter"}
            </button>

            <button
              className="ns-button ns-button-ghost"
              type="button"
              onClick={() =>
                setFrontMatterChoicesOpen(
                  false
                )
              }
            >
              {isFR
                ? "Annuler"
                : "Cancel"}
            </button>
          </div>
        </div>
      ) : null}

      {savedUnitNotice ? (
        <div
          role="status"
          style={{
            padding: "12px 14px",
            marginBottom: 18,
            borderRadius: 12,
            border:
              "1px solid rgba(22,101,52,0.28)",
            background: "#f0fdf4",
            color: "#166534",
            fontWeight: 800,
            lineHeight: 1.5,
          }}
        >
          {savedUnitNotice}
        </div>
      ) : null}

          {renderBookStructureRangeNavigation()}

      <div className="ns-nbb-major-grid" style={{ display: "grid", gap: 14 }}>
        {units.length === 0 ? (
          <div className="ns-card ns-nbb-major-panel" style={{ padding: 22, borderRadius: 16, textAlign: "center" }}>
            <strong>{isFR ? "Votre plan est encore vide" : "Your Book Structure Is Empty"}</strong>
            <p className="ns-card-meta" style={{ margin: "8px auto 0", maxWidth: 620, lineHeight: 1.6 }}>
              {isFR
                ? "Commencez par ajouter une page ou une double page. Le formulaire vous indiquera clairement les informations attendues."
                : "Begin by adding a Single Page or a Two-page Spread. The form will clearly explain what information belongs in each field."}
            </p>
          </div>
        ) : (
          visibleBookStructureRows.map(({ unit, index }) => (
            <article
              key={unit.id}
              data-testid="book-structure-unit-card"
              className="ns-card ns-nbb-major-panel"
              onDragOver={(event) => {
                if (
                  !draggingUnitId ||
                  draggingUnitId ===
                    unit.id
                ) {
                  return;
                }

                event.preventDefault();

                if (
                  event.dataTransfer
                ) {
                  event.dataTransfer.dropEffect =
                    "move";
                }

                setDragOverUnitId(
                  unit.id
                );
              }}
              onDrop={(event) =>
                dropBookStructureUnit(
                  event,
                  index
                )
              }
              style={{
                padding: 18,
                borderRadius: 16,
                outline:
                  dragOverUnitId ===
                    unit.id &&
                  draggingUnitId !==
                    unit.id
                    ? "2px dashed #2563eb"
                    : "none",
                outlineOffset: 2,
                opacity:
                  draggingUnitId ===
                    unit.id
                    ? 0.72
                    : 1,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
                <div>
                  <strong>
                    {childrensBookWriterPageLabel(
                      physicalPageItems[
                        index
                      ]
                    )}{unit.workingTitle ? " · " : ""}
                      {unit.workingTitle || ""}
                  </strong>
                  <p className="ns-card-meta" style={{ margin: "6px 0 0" }}>
                    {labelFor(statusOptions, unit.status)}
                  </p>

                  {renderUnitAttention(
                    unit
                  )}

                  <ChildrensBookIllustrationAssetPanel
                    compact
                    lang={lang}
                    unit={unit}
                    childrensBookAssets={
                      childrensBookAssets
                    }
                    onLoadChildrensBookAssetFile={
                      onLoadChildrensBookAssetFile
                    }
                  />
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <span
                    draggable
                    data-testid={
                      `book-structure-drag-handle-${unit.id}`
                    }
                    title={
                      isFR
                        ? "Faire glisser pour déplacer cette page ou double page"
                        : "Drag to move this page or spread"
                    }
                    onDragStart={(event) =>
                      beginBookStructureDrag(
                        event,
                        unit.id
                      )
                    }
                    onDragEnd={
                      finishBookStructureDrag
                    }
                    style={{
                      minHeight: 38,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "7px 10px",
                      borderRadius: 9,
                      border:
                        "1px dashed #94a3b8",
                      background: "#f8fafc",
                      fontWeight: 800,
                      cursor: "grab",
                      userSelect: "none",
                    }}
                  >
                    <span aria-hidden="true">
                      ⋮⋮
                    </span>

                    {isFR
                      ? "Glisser"
                      : "Drag"}
                  </span>

                  <button className="ns-button ns-button-outline" type="button" disabled={index === 0} onClick={() => moveUnit(index, -1)}>
                    {isFR ? "Plus tôt" : "Move Earlier"}
                  </button>
                  <button className="ns-button ns-button-outline" type="button" disabled={index === units.length - 1} onClick={() => moveUnit(index, 1)}>
                    {isFR ? "Plus tard" : "Move Later"}
                  </button>

                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    onClick={() => {
                      cancelRemoveUnit();

                      if (
                        movingUnitId ===
                        unit.id
                      ) {
                        setMovingUnitId("");
                        setMovePositionValue("");
                        return;
                      }

                      setMovingUnitId(
                        unit.id
                      );

                      setMovePositionValue(
                        String(
                          index + 1
                        )
                      );
                    }}
                  >
                    {isFR
                      ? "Déplacer vers…"
                      : "Move to Position"}
                  </button>
                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    onClick={() => {
                      setSavedUnitNotice("");
                      setEditingUnit(unit);
                    }}
                  >
                    {isFR ? "Modifier" : "Edit"}
                  </button>
                  <button className="ns-button ns-button-outline" type="button" onClick={() => duplicateUnit(unit)}>
                    {isFR ? "Dupliquer" : "Duplicate"}
                  </button>
                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    onClick={() =>
                      requestRemoveUnit(
                        unit.id
                      )
                    }
                  >
                    {isFR ? "Supprimer" : "Remove"}
                  </button>
                </div>
              </div>
              {movingUnitId === unit.id ? (
                <form
                  aria-label={
                    isFR
                      ? "Déplacer cette page ou double page"
                      : "Move this page or spread"
                  }
                  onSubmit={(event) =>
                    submitMoveToPosition(
                      event,
                      unit.id
                    )
                  }
                  style={{
                    display: "grid",
                    gap: 10,
                    marginTop: 14,
                    padding: 14,
                    borderRadius: 12,
                    border:
                      "1px solid rgba(37,99,235,0.25)",
                    background: "#f8fbff",
                  }}
                >
                  <strong>
                    {isFR
                      ? "Nouvelle position dans la Structure du livre"
                      : "New Book Structure position"}
                  </strong>

                  <p
                    className="ns-card-meta"
                    style={{
                      margin: 0,
                      lineHeight: 1.5,
                    }}
                  >
                    {isFR
                      ? `Choisissez une position entre 1 et ${units.length}.`
                      : `Choose a position from 1 to ${units.length}.`}
                  </p>

                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      flexWrap: "wrap",
                      alignItems: "end",
                    }}
                  >
                    <label
                      style={{
                        display: "grid",
                        gap: 6,
                        fontWeight: 800,
                      }}
                    >
                      <span>
                        {isFR
                          ? "Nouvelle position"
                          : "New position"}
                      </span>

                      <input
                        aria-label={
                          isFR
                            ? "Nouvelle position dans la Structure du livre"
                            : "New Book Structure position"
                        }
                        type="number"
                        min="1"
                        max={units.length}
                        step="1"
                        value={
                          movePositionValue
                        }
                        onChange={(event) =>
                          setMovePositionValue(
                            event.target.value
                          )
                        }
                        style={{
                          width: 120,
                          minHeight: 42,
                          padding:
                            "8px 10px",
                          borderRadius: 8,
                          border:
                            "1px solid #cbd5e1",
                        }}
                      />
                    </label>

                    <button
                      className="ns-button ns-button-outline"
                      type="button"
                      onClick={() => {
                        setMovingUnitId("");
                        setMovePositionValue("");
                      }}
                    >
                      {isFR
                        ? "Annuler"
                        : "Cancel"}
                    </button>

                    <button
                      className="ns-button ns-button-primary"
                      type="submit"
                    >
                      {isFR
                        ? "Déplacer"
                        : "Move"}
                    </button>
                  </div>
                </form>
              ) : null}

              {pendingRemovalId === unit.id ? (
                <form
                  role="alertdialog"
                  aria-modal="false"
                  aria-labelledby={
                    `childrens-book-remove-unit-title-${unit.id}`
                  }
                  aria-describedby={
                    `childrens-book-remove-unit-description-${unit.id}`
                  }
                  onSubmit={confirmRemoveUnit}
                  style={{
                    padding: 16,
                    marginTop: 14,
                    borderRadius: 14,
                    border:
                      "1px solid rgba(153,27,27,0.35)",
                    background: "#fef2f2",
                  }}
                >
                  <strong
                    id={
                      `childrens-book-remove-unit-title-${unit.id}`
                    }
                    style={{
                      display: "block",
                      fontSize: 16,
                      color: "#991b1b",
                    }}
                  >
                    {isFR
                      ? "Confirmer le retrait"
                      : "Confirm removal"}
                  </strong>

                  <p
                    id={
                      `childrens-book-remove-unit-description-${unit.id}`
                    }
                    className="ns-card-meta"
                    style={{
                      margin: "8px 0 0",
                      lineHeight: 1.55,
                    }}
                  >
                    {isFR
                      ? `Retirer « ${
                          unit.workingTitle ||
                          labelFor(
                            unitTypeOptions,
                            unit.unitType
                          )
                        } » du Plan du livre ? Les plans du manuscrit et des illustrations ne seront pas supprimés silencieusement. Si une illustration téléversée active est liée à cette unité, l’enregistrement sera refusé jusqu’à ce que cette illustration soit retirée.`
                      : `Remove “${
                          unit.workingTitle ||
                          labelFor(
                            unitTypeOptions,
                            unit.unitType
                          )
                        }” from the Book Structure? Manuscript and Illustration Plan records will not be silently deleted. If an active uploaded illustration is linked to this unit, saving will be refused until that illustration is removed.`}
                  </p>

                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      flexWrap: "wrap",
                      marginTop: 12,
                    }}
                  >
                    <button
                      className="ns-button ns-button-outline"
                      type="button"
                      onClick={cancelRemoveUnit}
                    >
                      {isFR
                        ? "Annuler"
                        : "Cancel"}
                    </button>

                    <button
                      className="ns-button"
                      type="submit"
                      style={{
                        background: "#991b1b",
                        borderColor: "#991b1b",
                        color: "#ffffff",
                      }}
                    >
                      {isFR
                        ? "Retirer du Plan du livre"
                        : "Remove from Book Structure"}
                    </button>
                  </div>
                </form>
              ) : null}

              <p style={{ margin: "12px 0 0", lineHeight: 1.6 }}>{displayValue(unit.purpose)}</p>
            </article>
          ))
        )}
      </div>


      <div className="ns-card" style={{ padding: 16, marginTop: 18, borderRadius: 16, background: "#fff7ed", border: "1px solid rgba(234,88,12,0.25)" }}>
        <strong>
          {persistenceEnabled
            ? (
                isFR
                  ? "Plan du livre dans le Blueprint"
                  : "Book Structure in the Blueprint"
              )
            : (
                isFR
                  ? "Plan local uniquement"
                  : "Local plan only"
              )}
        </strong>

        <p className="ns-card-meta" style={{ margin: "7px 0 0", lineHeight: 1.55 }}>
          {persistenceEnabled
            ? (
                isFR
                  ? "Les pages et doubles pages seront conservées lorsque vous enregistrerez le Blueprint. Les manuscrits et illustrations restent gérés dans leurs espaces de travail respectifs."
                  : "Pages and spreads are preserved when you save the Blueprint. Manuscripts and illustrations remain managed in their own workspaces."
              )
            : (
                isFR
                  ? "Ce plan reste uniquement dans la mémoire locale de cette page jusqu’à l’activation de l’enregistrement."
                  : "This plan remains only in this page’s local memory until saving is enabled."
              )}
        </p>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginTop: 18 }}>
        <button className="ns-button ns-button-outline" type="button" onClick={onReturn}>
          {returnToWholeBookReview
            ? (
                isFR
                  ? "Retour à la révision complète du livre"
                  : "Return to Whole Book Review"
              )
            : (
                isFR
                  ? "Retour au NextScenes Book Blueprint"
                  : "Return to NextScenes Book Blueprint"
              )}
        </button>
        <button className="ns-button ns-button-primary" type="button" onClick={() => setReviewingMap(true)}>
          {isFR ? "Vérifier le plan des pages" : "Review Page and Spread Plan"}
        </button>
      </div>
    </section>
  );
}
